import { resolveShiftTimeZone, US_TIME_ZONES } from '../core/shifts/shiftTimeZone';
import useOperationalClock from '../core/shifts/useOperationalClock';
import SalesWorkspace from '../modules/sales/SalesWorkspace';
import useSalesSession from '../modules/sales/useSalesSession';
import { createFacilityInvitation } from "../services/access/facilityInvitations";
import { getFinancialAccess, hydrateFinancialRecords, saveFacilityFinancialProfile } from "../core/finance/financialService";
import { financialViewAvailable } from "../enterprise/data/financialAvailability";
import React, { useEffect, useMemo, useState } from "react";
import { signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
  doc,
  updateDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { auth, db } from "../firebase";
import {
  roleColorFor,
} from "../core/shifts/shiftPresentation";
import {
  getShiftOperationalSummary,
} from "../core/shifts/shiftOperationalSummary";

import {
  buildShiftMetrics,
} from "../utils/shiftMetricsEngine";
import AdminCredentialManager from "../components/AdminCredentialManager";
import AdminWorkforceManager from "../components/AdminWorkforceManager";
import AdminMigrationTools from "../components/AdminMigrationTools";
import {
  PERMISSIONS,
  hasPermission,
  DESTRUCTIVE_PERMISSIONS,
} from "../security/permissions";
import {
  getEligibleShiftsForInvoice,
  buildInvoiceDraft,
  createInvoiceFromDraft,
  markInvoiceSent,
  markInvoicePaid,
  voidInvoice,
} from "../invoiceEngine";
import {
  canAccessTenant,
  canAccessFacility,
  canViewPayroll,
  canViewMargins,
  canViewBillRates,
  isOwnerAdmin,
  isAgencyAdmin,
  normalizeId,
} from "../utils/security/accessControl";
import backfillShiftTenants from "../utils/migrations/backfillShiftTenants";
import AdminComplianceCenter from "./AdminComplianceCenter";
import { useDateScope } from "../context/DateScopeContext";
import {
  archiveOldTestShifts,
  archiveOldTestWorkers,
} from "../utils/archiveTestData";
import { seedBridgeRehabWorkerInvites } from "../utils/seedBridgeWorkerInvites";
import {
  TalentPipeline,
  REQUIRED_DOCUMENTS,
  PIPELINE_ORDER,
} from "../modules/talentPipeline";
import ApplicantProvider from "../providers/ApplicantProvider";
import AdminInviteConsole from "../components/AdminInviteConsole";
import SystemCenter from "../components/SystemCenter";
import WorkflowNavigator from "../components/WorkflowNavigator";
import EnterpriseShell from "../enterprise/layouts/EnterpriseShell";
import RatesCenter from "../modules/finance/components/RatesCenter";
import PayrollCenter from "../modules/finance/components/PayrollCenter";
import InvoiceCenter from "../modules/finance/components/InvoiceCenter";
import PayrollStubDrawer from "../modules/finance/components/PayrollStubDrawer";
import HomeDashboard from "../modules/dashboard/components/ExecutiveDashboard";
import LoadingState from "../components/common/LoadingState.jsx";
import FundingCenter from "../modules/finance/components/FundingCenter";
import Facility360Overview from "../modules/facilities/components/Facility360Overview";
import Facility360Profile from "../modules/facilities/components/Facility360Profile";
import Facility360Workforce from "../modules/facilities/components/Facility360Workforce";
import Facility360Shifts from "../modules/facilities/components/Facility360Shifts";
import Facility360Financial from "../modules/facilities/components/Facility360Financial";
import Facility360Compliance from "../modules/facilities/components/Facility360Compliance";
import Facility360Activity from "../modules/facilities/components/Facility360Activity";
import Facility360AIInsights from "../modules/facilities/components/Facility360AIInsights";
import Facility360Invoices from "../modules/facilities/components/Facility360Invoices";
import Facility360Contacts from "../modules/facilities/components/Facility360Contacts";
import Facility360Documents from "../modules/facilities/components/Facility360Documents";
import Facility360Incidents from "../modules/facilities/components/Facility360Incidents";
import Facility360Notes from "../modules/facilities/components/Facility360Notes";
import Facility360KPI from "../modules/facilities/components/Facility360KPI";
import Facility360Communications from "../modules/facilities/components/Facility360Communications";
import OperationsCenter from "../modules/operations/components/OperationsCenter";
import { approveRegistration as approveWorkerRegistration } from "../core/workflow/WorkerStateEngine";
import {
  AutomationEvents,
  fireAutomation,
} from "../automation";
import { createPortal } from "react-dom";



function toMillis(v) {
  if (!v) return NaN;
  try {
    if (typeof v?.toDate === "function") return v.toDate().getTime();
    if (typeof v === "number") return v;
    return new Date(v).getTime();
  } catch {
    return NaN;
  }
}

function fmtDate(v) {
  const ms = toMillis(v);
  if (!Number.isFinite(ms)) return "—";
  return new Date(ms).toLocaleDateString();
}

function fmtDateTime(v) {
  const ms = toMillis(v);
  if (!Number.isFinite(ms)) return "—";
  return new Date(ms).toLocaleString();
}

function fmtMoney(v) {
  const n = Number(v || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n);
}

function fmtLogTime(v) {
  const ms = toMillis(v);
  if (!Number.isFinite(ms)) return "—";
  return new Date(ms).toLocaleString();
}

function niceAction(v) {
  const key = String(v || "")
    .replace(/_/g, " ")
    .trim();

  return key ? key.replace(/\b\w/g, (m) => m.toUpperCase()) : "Action";
}

function fmtHours(v) {
  const n = Number(v || 0);
  return `${n.toFixed(2)} hrs`;
}

function fmtTime(ts) {
  if (!ts) return "";

  const d = ts?.toDate ? ts.toDate() : new Date(ts);

  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function startOfWeek(dateLike = new Date()) {
  const d = new Date(dateLike);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfWeek(dateLike = new Date()) {
  const s = startOfWeek(dateLike);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
}

function ymd(dateLike) {
  const d = new Date(dateLike);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function roleKey(v) {
  return String(v || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_");
}

function niceRole(v) {
  return String(v || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function getAssignedUid(shift) {
  if (shift?.assignedWorkerUid) return shift.assignedWorkerUid;
  if (Array.isArray(shift?.assignedWorkerUids) && shift.assignedWorkerUids.length > 0) {
    return shift.assignedWorkerUids[0];
  }
  return "";
}

function shiftHours(shift) {
  const s = toMillis(shift?.startTime);
  const e = toMillis(shift?.endTime);
  if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return 0;
  return (e - s) / 3600000;
}

function statusTone(
  status,
  operational = null
) {
  const s = String(
    status || ""
  ).toLowerCase();

  if (
    operational
      ?.awaitingAcceptance
  ) {
    return {
      bg: "#fff7ed",
      bd: "#fed7aa",
      fg: "#9a3412",
    };
  }

  if (s === "completed") {
    return {
      bg: "#dcfce7",
      bd: "#86efac",
      fg: "#166534",
    };
  }

  if (s === "arrived") {
    return {
      bg: "#ecfdf5",
      bd: "#a7f3d0",
      fg: "#047857",
    };
  }

  if (s === "tracking") {
    return {
      bg: "#f5f3ff",
      bd: "#ddd6fe",
      fg: "#6d28d9",
    };
  }

  if (s === "activated") {
    return {
      bg: "#eff6ff",
      bd: "#bfdbfe",
      fg: "#1d4ed8",
    };
  }

  if (s === "assigned") {
    return {
      bg: "#eef2ff",
      bd: "#c7d2fe",
      fg: "#4338ca",
    };
  }

  if (
    [
      "open",
      "offered",
      "requested",
      "time_required",
    ].includes(s)
  ) {
    return {
      bg: "#fff7ed",
      bd: "#fed7aa",
      fg: "#9a3412",
    };
  }

  return {
    bg: "#f3f4f6",
    bd: "#d1d5db",
    fg: "#111827",
  };
}

function getAdminShiftStatusLabel(
  shift
) {
  const operational =
    getShiftOperationalSummary(
      shift
    );

  if (
    operational
      .awaitingAcceptance
  ) {
    return "PENDING COMMITMENT";
  }

  if (
    operational.status ===
      "assigned" &&
    operational.accepted
  ) {
    return "CONFIRMED";
  }

  if (
    operational.status ===
    "activated"
  ) {
    return "ACTIVE";
  }

  if (
    operational.status ===
    "tracking"
  ) {
    return "EN ROUTE";
  }

  if (
    operational.status ===
    "arrived"
  ) {
    return operational.operationalLabel.toUpperCase();
  }

  if (
    operational.status ===
    "completed"
  ) {
    return "COMPLETED";
  }

  return String(
    operational
      .operationalLabel ||
      operational.status ||
      "Open"
  ).toUpperCase();
}

function cardStyle() {
  return {
    background: "#fff",
    border: "1px solid #d8e1ea",
    borderRadius: 16,
    boxShadow: "0 8px 22px rgba(15,23,42,.05)",
  };
}

function buttonBaseStyle() {
  return {
    border: "1px solid #d8e1ea",
    background: "#fff",
    color: "#0f172a",
    borderRadius: 10,
    padding: "9px 12px",
    fontWeight: 900,
    fontSize: 12,
    cursor: "pointer",
  };
}

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 12,
  marginTop: 16,
};

const metricCardStyle = {
  background: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: 14,
};

const metricNumberStyle = {
  fontSize: 26,
  fontWeight: 800,
  marginTop: 6,
};

const rowCardStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: 14,
  marginTop: 10,
  background: "#ffffff",
};

const sectionLabelStyle = {
  marginLeft: 12,
  marginRight: 6,
  fontSize: 11,
  fontWeight: 800,
  color: "#64748b",
  letterSpacing: 1,
};

function primaryButtonStyle() {
  return {
    ...buttonBaseStyle(),
    background: "#2563eb",
    color: "#fff",
    border: "1px solid #2563eb",
  };
}

function sectionTitle(text, subtext = "") {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
      }}
    >
      <div>
        <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>{text}</div>
        {subtext ? (
          <div
            style={{
              color: "#64748b",
              fontSize: 12,
              fontWeight: 700,
              marginTop: 2,
            }}
          >
            {subtext}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MetricCard({ label, value, helper }) {
  return (
    <div style={{ ...cardStyle(), padding: 14 }}>
      <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color: "#0f172a",
          fontSize: 26,
          fontWeight: 900,
          lineHeight: 1,
        }}
      >
        {value}
      </div>

      {helper ? (
        <div
          style={{
            marginTop: 8,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {helper}
        </div>
      ) : null}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
  badge = 0,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: active ? "2px solid #2563eb" : "1px solid #d8e1ea",
        background: active ? "#eff6ff" : "#fff",
        color: active ? "#1d4ed8" : "#334155",
        borderRadius: 999,
        padding: "8px 14px",
        fontWeight: 900,
        fontSize: 12,
        cursor: "pointer",
        minHeight: 38,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        position: "relative",
      }}
    >
      <span>{children}</span>

      {badge > 0 ? (
        <span
          style={{
            minWidth: 20,
            height: 20,
            borderRadius: 999,
            background: "#ef4444",
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontSize: 11,
            fontWeight: 900,
            padding: "0 6px",
            lineHeight: 1,
            boxShadow: "0 2px 6px rgba(239,68,68,.35)",
          }}
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}

function normalizeBillingRatesFromProfile(profile) {
  const rates = profile?.rates || {};

  return {
    CNA: Number(rates?.CNA?.billRate ?? 0),
    LVN: Number(rates?.LVN?.billRate ?? 0),
    LPN: Number(rates?.LPN?.billRate ?? 0),
    RN: Number(rates?.RN?.billRate ?? 0),
    MED_AIDE: Number(rates?.MED_AIDE?.billRate ?? 0),
    CARE_GIVER: Number(rates?.CARE_GIVER?.billRate ?? 0),
  };
}

function fieldLabelStyle() {
  return {
    fontSize: 11,
    fontWeight: 900,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: ".04em",
    marginBottom: 6,
  };
}

function inputStyle() {
  return {
    width: "100%",
    height: 40,
    borderRadius: 12,
    border: "1px solid #cbd5e1",
    padding: "0 12px",
    fontSize: 13,
    fontWeight: 800,
    color: "#0f172a",
    background: "#ffffff",
    boxSizing: "border-box",
  };
}


const FACTORING_COMPANIES = [
  {
    id: "riviera",
    name: "Riviera Finance",
    type: "factoring",
    defaultAdvanceRate: 0.8,
    defaultFeePercent: 3,
  },
  {
    id: "kd_factors",
    name: "KD Factors",
    type: "factoring",
    defaultAdvanceRate: 0.8,
    defaultFeePercent: 3,
  },
  {
    id: "altline",
    name: "altLINE",
    type: "factoring",
    defaultAdvanceRate: 0.85,
    defaultFeePercent: 2.5,
  },
];

const ENTERPRISE_NAV_ITEMS = [
  { key: "overview", label: "Home" },
  { key: "operations", label: "Operations" },
  { key: "facilities", label: "Facilities" },
  { key: "workers", label: "Workers" },
  { key: "invitations", label: "Invitations" },
  { key: "finance", label: "Finance" },
  { key: "sales", label: "Sales" },
  { key: "users_access", label: "Users & Access" },
  { key: "system", label: "Settings" },
];


export default function AdminDashboardEnterprise({ profile = {} }) {
useOperationalClock();
const salesSession = useSalesSession();

async function approveRegistration(worker) {
  try {
    await approveWorkerRegistration(worker);

    alert("Registration approved.");
  } catch (err) {
    console.error("approveRegistration error:", err);
    alert(err?.message || "Failed to approve registration.");
  }
}

async function approveWorker(workerId) {
  try {
    const worker = users.find(
      (u) => u.uid === workerId || u.id === workerId
    );

    if (!worker) {
      alert("Worker not found.");
      return;
    }

    const docs =
      worker.credentialMap ||
      worker.documents ||
      worker.uploadedDocs ||
      {};

    const allDocsApproved = REQUIRED_DOCUMENTS.every(([key]) => {
      return docs?.[key]?.status === "approved";
    });

    if (!allDocsApproved) {
      alert(
        "Cannot approve worker. All required documents must be reviewed and approved first."
      );

      setSelectedOnboardingWorker(worker);
      setActiveTab("workers");
      setWorkersTab("pipeline");
      return;
    }

    await updateDoc(doc(db, "users", workerId), {
      onboardingStatus: "approved",
      onboardingCompleted: true,
      complianceStatus: "approved",
      workerStatus: "ready",
      workerOperationalState: "READY",
      status: "active",
      approvedAt: serverTimestamp(),
      approvedBy: auth.currentUser?.uid || "admin",
      updatedAt: serverTimestamp(),
    });

    await setDoc(
  doc(db, "invites", workerId),
  {
    archived: true,
    archivedAt: serverTimestamp(),
    status: "completed",
    inviteStatus: "completed",
    invitationStatus: "completed",
    onboardingStatus: "approved",
    workerStatus: "ready",
    complianceStatus: "approved",
    updatedAt: serverTimestamp(),
  },
  { merge: true }
);

    await fireAutomation(
      AutomationEvents.WORKER_APPROVED,
      {
        uid: workerId,
        email: worker.email || "",
        displayName:
          worker.displayName ||
          worker.fullName ||
          [worker.firstName, worker.lastName]
            .filter(Boolean)
            .join(" ") ||
          "",
        facilityId:
          worker.facilityId ||
          worker.primaryFacilityId ||
          "",
        tenantId:
          worker.tenantId ||
          worker.tenantKey ||
          "dogebridge",
        approvedBy:
          auth.currentUser?.uid ||
          auth.currentUser?.email ||
          "admin",
      }
    );

    alert("Worker approved successfully.");
  } catch (err) {
    console.error("approveWorker error:", err);
    alert(err?.message || "Failed to approve worker.");
  }
}

  async function reviewWorkerDocument(workerUid, docKey, status) {
  if (!workerUid || !docKey) return;

  try {
    await updateDoc(doc(db, "workers", workerUid), {
      [`documents.${docKey}.status`]: status,
      [`documents.${docKey}.reviewedAt`]: serverTimestamp(),
      [`documents.${docKey}.reviewedBy`]: auth.currentUser?.uid || "admin",
    });
  } catch (err) {
    console.error("Review worker document error:", err);
  }
}

async function handleBackfillShiftTenants() {
  try {
    setError("");

    const confirmed = window.confirm(
      "This will backfill missing tenant fields on existing shifts. Continue?"
    );

    if (!confirmed) return;

    const count = await backfillShiftTenants();

    alert(`Tenant backfill completed. Updated ${count} shifts.`);
  } catch (err) {
    console.error(err);
    setError(err?.message || "Tenant backfill failed.");
  }
}

  const [activeTab, setActiveTab] = useState("overview");
  const [onboardingFilter, setOnboardingFilter] = useState("all");
const [selectedOnboardingWorker, setSelectedOnboardingWorker] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);

const [inviteRecords, setInviteRecords] = useState([]);

const [invoices, setInvoices] = useState([]);
const [incidents, setIncidents] = useState([]);
const [communications, setCommunications] = useState([]);

  const [adminLogs, setAdminLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attentionTarget, setAttentionTarget] = useState("");

const [showInternalAdminInvite, setShowInternalAdminInvite] =
  useState(false);

const [savingInternalAdminInvite, setSavingInternalAdminInvite] =
  useState(false);

const [internalAdminInviteMessage, setInternalAdminInviteMessage] =
  useState("");

const [internalAdminInviteForm, setInternalAdminInviteForm] =
  useState({
    name: "",
    email: "",
    phone: "",
    role: "operations_admin",
    accessScope: "all_facilities",
    allowedFacilityIds: [],
  });

const [operationsTab, setOperationsTab] = useState("planner");
const [workersTab, setWorkersTab] = useState("pipeline");
const [financeTab, setFinanceTab] = useState("invoices");
const [financialViewReady, setFinancialViewReady] = useState(false);
const [facilitiesTab, setFacilitiesTab] = useState("manage");

  const defaultInvoiceFrom = ymd(startOfWeek(new Date()));
  const defaultInvoiceTo = ymd(endOfWeek(new Date()));

  const [invoiceDateFrom, setInvoiceDateFrom] = useState(defaultInvoiceFrom);
  const [invoiceDateTo, setInvoiceDateTo] = useState(defaultInvoiceTo);

 const [selectedFacility, setSelectedFacility] = useState("all");
const profileTenantId = normalizeId(profile?.tenantId);

const isTenantInternalAdmin = profile?.trustedPlatformAdmin !== true;

const [selectedTenant, setSelectedTenant] = useState(() =>
  isTenantInternalAdmin
    ? profileTenantId
    : "all"
);

useEffect(() => {
  if (
    isTenantInternalAdmin &&
    selectedTenant !== profileTenantId
  ) {
    setSelectedTenant(profileTenantId);
  }
}, [
  isTenantInternalAdmin,
  profileTenantId,
  selectedTenant,
]);
const [tenantRecords, setTenantRecords] = useState([]);

  const [sending, setSending] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1280
  );

  const [billingRates, setBillingRates] = useState({
    CNA: 23,
    LVN: 42,
    LPN: 42,
    RN: 55,
    MED_AIDE: 24,
    CARE_GIVER: 22,
  });

  const [showBillingRates, setShowBillingRates] = useState(false);
  const [activitySearch, setActivitySearch] = useState("");
  const [activityActionFilter, setActivityActionFilter] = useState("all");
  const [activityDays, setActivityDays] = useState("30");
  const [invoiceFacilityId, setInvoiceFacilityId] = useState("all");
  const [invoiceStart, setInvoiceStart] = useState("");
  const [invoiceEnd, setInvoiceEnd] = useState("");
  const [invoicePreview, setInvoicePreview] = useState([]);
  const [invoiceBusy, setInvoiceBusy] = useState(false);

 const [adminLogFilter, setAdminLogFilter] = useState("30D");

  const [editingTenant, setEditingTenant] = useState(null);

const [editingFacility, setEditingFacility] = useState(null);
const [creatingFacility, setCreatingFacility] = useState(false);

const [managingFacilityAccounts, setManagingFacilityAccounts] =
  useState(null);

const [facilityAccountEmail, setFacilityAccountEmail] = useState("");
const [facilityAccountName, setFacilityAccountName] = useState("");
const [facilityAccountPhone, setFacilityAccountPhone] = useState("");

const [facilityAccountRole, setFacilityAccountRole] =
  useState("facility_planner");

const [savingFacilityAccount, setSavingFacilityAccount] =
  useState(false);

const [facilityAccountMessage, setFacilityAccountMessage] =
  useState("");

const [facility360Section, setFacility360Section] =
  useState("overview");

const [showPayrollStub, setShowPayrollStub] = useState(false);

const [selectedPayrollShift, setSelectedPayrollShift] = useState(null);

const [selectedFundingCompany, setSelectedFundingCompany] =
  useState("riviera");
  
const invitedUsers = useMemo(() => {
  const workerRoles = new Set([
    "worker",
    "nurse",
    "caregiver",
  ]);

  const workerCredentials = new Set([
    "CNA",
    "LVN",
    "LPN",
    "RN",
    "MED_AIDE",
    "CARE_GIVER",
    "CAREGIVER",
  ]);

  const isWorkerRecord = (record) => {
    const role = String(
      record?.platformRole ||
        record?.role ||
        record?.accountType ||
        ""
    )
      .trim()
      .toLowerCase();

    const credential = String(
      record?.workerType ||
        record?.position ||
        record?.primaryRole ||
        ""
    )
      .trim()
      .toUpperCase();

    return (
      workerRoles.has(role) ||
      workerCredentials.has(credential)
    );
  };

  const userEmails = new Set(
    (users || [])
      .filter(isWorkerRecord)
      .filter((user) => {
        const onboardingStatus = String(
          user?.onboardingStatus || ""
        ).toLowerCase();

        const workerStatus = String(
          user?.workerStatus || ""
        ).toLowerCase();

        const status = String(
          user?.status || ""
        ).toLowerCase();

        const registrationStatus = String(
          user?.registrationStatus || ""
        ).toLowerCase();

        return (
          registrationStatus === "approved" ||
          [
            "registered",
            "onboarding",
            "missing_docs",
            "in_progress",
            "submitted",
            "pending_review",
            "approved",
          ].includes(onboardingStatus) ||
          [
            "onboarding",
            "under_review",
            "ready",
          ].includes(workerStatus) ||
          status === "active"
        );
      })
      .map((user) =>
        String(user?.email || "")
          .trim()
          .toLowerCase()
      )
      .filter(Boolean)
  );

  return (inviteRecords || []).filter((invite) => {
    if (invite?.archived === true) return false;

    // Facility accounts and internal admins must never
    // enter the worker Talent Pipeline.
    if (!isWorkerRecord(invite)) return false;

    const inviteTenantId = normalizeId(
      invite?.tenantId
    );

    if (
      selectedTenant !== "all" &&
      inviteTenantId !== normalizeId(selectedTenant)
    ) {
      return false;
    }

    const email = String(invite?.email || "")
      .trim()
      .toLowerCase();

    if (email && userEmails.has(email)) {
      return false;
    }

    const inviteStatus = String(
      invite?.inviteStatus ||
        invite?.invitationStatus ||
        invite?.status ||
        ""
    ).toLowerCase();

    return [
      "invited",
      "sent",
      "pending",
    ].includes(inviteStatus);
  });
}, [
  inviteRecords,
  users,
  selectedTenant,
]);

const facilityAccountRecords = useMemo(() => {
  if (!managingFacilityAccounts) return [];

  const selectedFacilityId = String(
    managingFacilityAccounts.facilityId ||
      managingFacilityAccounts.id ||
      ""
  )
    .trim()
    .toLowerCase();

  if (!selectedFacilityId) return [];

  const facilityRoles = new Set([
    "facility_planner",
    "facilityplanner",
    "planner",
    "facility_admin_viewer",
    "facility_pulse",
  ]);

  const belongsToSelectedFacility = (record) => {
    const primaryFacilityId = String(
      record?.facilityId ||
        record?.primaryFacilityId ||
        ""
    )
      .trim()
      .toLowerCase();

    const assignedFacilityIds = [
      ...(Array.isArray(record?.facilityIds)
        ? record.facilityIds
        : []),
      ...(Array.isArray(record?.allowedFacilityIds)
        ? record.allowedFacilityIds
        : []),
      ...(Array.isArray(record?.assignedFacilityIds)
        ? record.assignedFacilityIds
        : []),
    ].map((facilityId) =>
      String(facilityId || "")
        .trim()
        .toLowerCase()
    );

    return (
      primaryFacilityId === selectedFacilityId ||
      assignedFacilityIds.includes(selectedFacilityId)
    );
  };

  const isFacilityAccount = (record) => {
    const role = String(
      record?.platformRole ||
        record?.role ||
        record?.accountType ||
        ""
    )
      .trim()
      .toLowerCase();

    return facilityRoles.has(role);
  };

  const accountMap = new Map();

  (inviteRecords || [])
    .filter((invite) => !invite?.archived)
    .filter(isFacilityAccount)
    .filter(belongsToSelectedFacility)
    .forEach((invite) => {
      const email = String(
        invite?.emailLower || invite?.email || ""
      )
        .trim()
        .toLowerCase();

      const key = email || `invite-${invite?.id}`;

      accountMap.set(key, {
        ...invite,
        email,
        recordType: "invitation",
        displayStatus: String(
          invite?.inviteStatus ||
            invite?.invitationStatus ||
            invite?.status ||
            "sent"
        ).toLowerCase(),
      });
    });

  (users || [])
    .filter((user) => !user?.archived)
    .filter(isFacilityAccount)
    .filter(belongsToSelectedFacility)
    .forEach((user) => {
      const email = String(
        user?.emailLower || user?.email || ""
      )
        .trim()
        .toLowerCase();

      const key =
        email ||
        String(user?.uid || user?.id || "").trim();

      if (!key) return;

      accountMap.set(key, {
        ...accountMap.get(key),
        ...user,
        email,
        recordType: "user",
        displayStatus: user?.disabled
          ? "disabled"
          : String(user?.status || "active").toLowerCase(),
      });
    });

  return Array.from(accountMap.values()).sort((a, b) => {
    const nameA = String(
      a?.name || a?.displayName || a?.email || ""
    ).toLowerCase();

    const nameB = String(
      b?.name || b?.displayName || b?.email || ""
    ).toLowerCase();

    return nameA.localeCompare(nameB);
  });
}, [inviteRecords, users, managingFacilityAccounts]);



  const canViewRates =
  hasPermission(profile, "canViewRates") ||
  canViewBillRates(profile);
  const canEditRates = hasPermission(profile, "canEditRates");
  const canGenerateInvoices = hasPermission(profile, "canGenerateInvoices");
  const canVoidInvoices = hasPermission(
    profile,
    DESTRUCTIVE_PERMISSIONS.VOID_INVOICE
  );
  const canMarkInvoicesPaid = hasPermission(
    profile,
    DESTRUCTIVE_PERMISSIONS.MARK_INVOICE_PAID
  );
  const canManageUsers = hasPermission(
    profile,
    DESTRUCTIVE_PERMISSIONS.MANAGE_USERS
  );
  const canViewAdminLogs = hasPermission(profile, "canViewAdminLogs");
  const canViewFinancialProjections = hasPermission(
    profile,
    "canViewFinancialProjections"
  );

  async function handleGenerateInvoice(draft) {
    try {
      setInvoiceBusy(true);
      setError("");

      const created = await createInvoiceFromDraft(
        draft,
        auth.currentUser?.uid || "admin"
      );

      alert(`Invoice created: ${created.invoiceNumber}`);
      setInvoicePreview([]);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Generate failed");
    } finally {
      setInvoiceBusy(false);
    }
  }

  async function handleMarkInvoiceSent(id) {
    await markInvoiceSent(id);
    alert("Marked sent");
  }

  async function handleMarkInvoicePaid(id) {
    if (!canMarkInvoicesPaid) {
      setError("You do not have permission to mark invoices paid.");
      return;
    }

    await markInvoicePaid(id);
    alert("Marked paid");
  }

  async function handleVoidInvoice(id) {
    if (!canVoidInvoices) {
      setError("You do not have permission to void invoices.");
      return;
    }

    await voidInvoice(id);
    alert("Voided");
  }

  async function handleArchiveOldTestShifts() {
  try {
    setError("");
    const count = await archiveOldTestShifts();
    alert(`Archived ${count} old shifts.`);
  } catch (err) {
    console.error(err);
    setError(err?.message || "Failed to archive old shifts.");
  }
}

async function handleArchiveOldTestWorkers() {
  try {
    setError("");
    const count = await archiveOldTestWorkers();
    alert(`Archived ${count} old workers.`);
  } catch (err) {
    console.error(err);
    setError(err?.message || "Failed to archive old workers.");
  }
}

async function handleSeedBridgeWorkerInvites() {
  try {
    setError("");
    const count = await seedBridgeRehabWorkerInvites();
    alert(`Created ${count} Bridge Rehab worker invites.`);
  } catch (err) {
    console.error(err);
    setError(err?.message || "Failed to create Bridge Rehab worker invites.");
  }
}


const facilitiesById = useMemo(() => {
  const map = new Map();

  (facilities || []).forEach((facility) => {
    const key = normalizeId(
      facility?.id || facility?.facilityId
    );

    if (!key) return;

    map.set(key, facility);
  });

  return map;
}, [facilities]);

const adminFacilityId = useMemo(() => {
  return selectedFacility === "all"
    ? ""
    : String(selectedFacility || "").toLowerCase();
}, [selectedFacility]);

const adminActiveContext = useMemo(() => {
  return {
    tab: activeTab,
    mode: "manual",
    facilityId: adminFacilityId,
  };
}, [activeTab, adminFacilityId]);

function isCompletedShift(s) {
  const status = String(s.status || "").toLowerCase();

  return (
    status === "completed" ||
    s.completed === true ||
    s.lifecycleStatus === "completed"
  );
}

function isActiveCompletedShift(s) {
  return (
    isCompletedShift(s) &&
    !s.archived &&
    !s.testData
  );
}

const payrollShifts = useMemo(() => (shifts || []).filter(isActiveCompletedShift), [shifts]);

  useEffect(() => {
  const facilityIdToUse =
    invoiceFacilityId && invoiceFacilityId !== "all"
      ? String(invoiceFacilityId).toLowerCase()
      : selectedFacility && selectedFacility !== "all"
      ? String(selectedFacility).toLowerCase()
      : "";

  if (!facilityIdToUse) return;

 const facilityProfile = facilitiesById.get(facilityIdToUse);
  if (!facilityProfile) return;

  setBillingRates(normalizeBillingRatesFromProfile(facilityProfile));
  setShowBillingRates(true);
}, [invoiceFacilityId, selectedFacility, facilitiesById]);

  useEffect(() => {
    if (!invoiceStart || !invoiceEnd) {
      const now = new Date();

      const start = new Date(now);
      start.setDate(now.getDate() - 7);
      start.setHours(0, 0, 0, 0);

      const end = new Date(now);
      end.setHours(23, 59, 59, 999);

      setInvoiceStart(start.toISOString().slice(0, 16));
      setInvoiceEnd(end.toISOString().slice(0, 16));
    }
  }, [invoiceStart, invoiceEnd]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    if (typeof window !== "undefined") {
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }
    return undefined;
  }, []);


useEffect(() => {
  let unsubShifts = () => {};
  let unsubFinancial = () => {};
  let unsubUsers = () => {};
  let unsubInvoices = () => {};
  let unsubIncidents = () => {};
  let unsubCommunications = () => {};
  let unsubAdminLogs = () => {};

  let unsubInvites = () => {};
  
async function boot() {
  setLoading(true);
  setFinancialViewReady(false);
  setError("");

  try {
  const profileOperationalTenantId = normalizeId(profile?.tenantId);
const selectedOperationalTenantId = normalizeId(selectedTenant);

const operationalTenantId =
  selectedOperationalTenantId &&
  selectedOperationalTenantId !== "all"
    ? selectedOperationalTenantId
    : profileOperationalTenantId;

if (!operationalTenantId || operationalTenantId === "all" ||
    (profile?.trustedPlatformAdmin !== true && operationalTenantId !== profileOperationalTenantId)) {
  throw new Error(
    "Enterprise operational tenant is not configured for this account."
  );
}

    let financialAccess = null;

    try {
      financialAccess = await getFinancialAccess();
    } catch (financialErr) {
      console.error("ENTERPRISE FINANCIAL ACCESS ERROR", financialErr);
    }

    setFinancialViewReady(await financialViewAvailable(
      financialAccess, operationalTenantId,
      async tenantId => (await getDoc(doc(db, "financialSecurity", tenantId))).data()
    ));

    const facilitySnap = await getDocs(
      query(
        collection(db, "facilityProfiles"),
        where("tenantId", "==", operationalTenantId)
      )
    );

    const facilityRows = facilitySnap.docs
      .map((d) => {
        const data = d.data() || {};
        const id = d.id;

        return {
          id,
          facilityId:
            data.facilityId || id,
          name:
            data.name ||
            data.facilityName ||
            id,
          facilityName:
            data.facilityName ||
            data.name ||
            id,
          ...data,
        };
      })
      .sort((a, b) =>
        String(
          a.name ||
            a.facilityName ||
            a.id
        ).localeCompare(
          String(
            b.name ||
              b.facilityName ||
              b.id
          )
        )
      );

    if (financialAccess) {
      try {
        setFacilities(
          await hydrateFinancialRecords(
            "facilityProfiles",
            facilityRows,
            financialAccess
          )
        );
      } catch (financialErr) {
        console.error(
          "ENTERPRISE FACILITY FINANCIAL HYDRATION ERROR",
          financialErr
        );
        setFacilities(facilityRows);
      }
    } else {
      setFacilities(facilityRows);
    }

    if (
      financialAccess &&
      ["owner_admin", "admin", "finance_admin"].includes(financialAccess.role)
    ) {
      unsubFinancial = onSnapshot(
        query(
          collection(db, "privateFinancials"),
          where("tenantId", "==", financialAccess.tenantId),
          where("kind", "==", "shifts")
        ),
        (snapshot) => {
          const values = new Map(
            snapshot.docs.map((item) => [
              item.data().sourceId,
              item.data().data,
            ])
          );

          setShifts((previous) =>
            previous.map((row) => ({
              ...row,
              ...(values.get(row.id) || {}),
            }))
          );
        },
        (financialErr) => {
          console.error(
            "ENTERPRISE PRIVATE FINANCIALS LISTENER ERROR",
            financialErr
          );
        }
      );
    }

    unsubShifts = onSnapshot(
      query(
        collection(db, "shifts"),
        where("tenantId", "==", operationalTenantId),
        orderBy("startTime", "desc")
      ),
      async (snap) => {
        const shiftRows = snap.docs
          .map((d) => ({
            id: d.id,
            ...d.data(),
          }))
          .filter((shift) => !shift.archived);

        if (!financialAccess) {
          setShifts(shiftRows);
          return;
        }

        try {
          setShifts(
            await hydrateFinancialRecords(
              "shifts",
              shiftRows,
              financialAccess
            )
          );
        } catch (financialErr) {
          console.error(
            "ENTERPRISE SHIFT FINANCIAL HYDRATION ERROR",
            financialErr
          );
          setShifts(shiftRows);
        }
      },
      (err) => {
        console.error("ENTERPRISE SHIFTS LISTENER ERROR", err);
        setError(
          `Shifts error: ${err?.code || ""} ${err?.message || ""}`
        );
      }
    );

    unsubUsers = onSnapshot(
      query(
        collection(db, "users"),
        where("tenantId", "==", operationalTenantId),
        orderBy("email", "asc")
      ),
      (snap) => {
        setUsers(
          snap.docs
            .map((d) => {
              const data = d.data() || {};

              return {
                id: d.id,
                uid: d.id,
                ...data,
                facilityId:
                  data.facilityId ||
                  data.primaryFacilityId ||
                  data.assignedFacilityId ||
                  "",
                facilityName:
                  data.facilityName ||
                  data.primaryFacilityName ||
                  data.assignedFacilityName ||
                  "",
                invitationStatus:
                  data.invitationStatus ||
                  data.inviteStatus ||
                  data.onboardingInviteStatus ||
                  "not_submitted",
              };
            })
            .filter((u) => !u.archived)
        );
      },
      (err) => {
        console.error("ENTERPRISE USERS LISTENER ERROR", err);
        setError(
          `Users error: ${err?.code || ""} ${err?.message || ""}`
        );
      }
    );


unsubInvites = onSnapshot(
  query(collection(db, "invites"), where("tenantId", "==", operationalTenantId)),
  (snap) => {
    const rows = snap.docs
      .map((d) => ({
        id: d.id,
        uid: d.id,
        ...d.data(),
      }))
      .filter((invite) => invite.archived !== true);

    console.table(
      rows.map((invite) => ({
        email: invite.email,
        status: invite.status,
        inviteStatus: invite.inviteStatus,
        invitationStatus: invite.invitationStatus,
        registrationStatus: invite.registrationStatus,
        onboardingStatus: invite.onboardingStatus,
        workerStatus: invite.workerStatus,
        complianceStatus: invite.complianceStatus,
      }))
    );

    // Keep the full active invite collection.
    // Filtering is handled separately for the Invite Console and Pipeline.
    setInviteRecords(rows);
  },
  (err) =>
    setError(
      `Invites error: ${err?.code || ""} ${err?.message || ""}`
    )
);

unsubInvoices = onSnapshot(
  query(
    collection(db, "invoices"),
    where("tenantId", "==", operationalTenantId),
    orderBy("createdAt", "desc")
  ),
  (snap) => setInvoices(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  (err) => setError(`Invoices error: ${err?.code || ""} ${err?.message || ""}`)
);

unsubIncidents = onSnapshot(
  query(collection(db, "incidents"), where("tenantId", "==", operationalTenantId)),
  (snap) =>
    setIncidents(
      snap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }))
        .sort((a, b) => {
          const aTime = toMillis(
            a.incidentDate ||
              a.occurredAt ||
              a.createdAt
          );

          const bTime = toMillis(
            b.incidentDate ||
              b.occurredAt ||
              b.createdAt
          );

          return (
            (Number.isFinite(bTime) ? bTime : 0) -
            (Number.isFinite(aTime) ? aTime : 0)
          );
        })
    ),
(err) => {
  console.error("INCIDENTS LISTENER ERROR", err);

  setError(
    `Incidents error: ${err?.code || ""} ${
      err?.message || ""
    }`
  );
}
);

unsubCommunications = onSnapshot(
  query(collection(db, "communications"), where("tenantId", "==", operationalTenantId)),
  (snap) =>
    setCommunications(
      snap.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
        }))
        .sort((a, b) => {
          const aTime = toMillis(
            a.sentAt ||
              a.createdAt ||
              a.updatedAt
          );

          const bTime = toMillis(
            b.sentAt ||
              b.createdAt ||
              b.updatedAt
          );

          return (
            (Number.isFinite(bTime) ? bTime : 0) -
            (Number.isFinite(aTime) ? aTime : 0)
          );
        })
    ),
(err) => {
  console.error(
    "COMMUNICATIONS LISTENER ERROR",
    err
  );

  setError(
    `Communications error: ${
      err?.code || ""
    } ${err?.message || ""}`
  );
}
);

unsubAdminLogs = onSnapshot(
  query(
    collection(db, "adminLogs"),
    where("tenantId", "==", operationalTenantId),
    orderBy("createdAt", "desc")
  ),
  (snap) => setAdminLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
  (err) => setError(`Admin logs error: ${err?.code || ""} ${err?.message || ""}`)
);


      } catch (err) {
       setError(`ADMIN INIT ERROR: ${err?.code || ""} ${err?.message || ""}`);
      } finally {
        setLoading(false);
      }
    }

    boot();

 return () => {
  unsubShifts();
  unsubFinancial();
  unsubUsers();
  unsubInvoices();
  unsubIncidents();
  unsubCommunications();
  unsubAdminLogs();

  unsubInvites();
  
};
  }, [profile?.tenantId, selectedTenant]);

  const usersByUid = useMemo(() => {
    const map = new Map();

    (users || []).forEach((user) => {
      const uid = user?.uid || user?.id;

      if (!uid) return;

      map.set(uid, user);
    });

    return map;
  }, [users]);

const registrationApprovalWorkers = (users || []).filter((u) => {
  const role = String(u.role || "").toLowerCase();
  const platformRole = String(u.platformRole || "").toLowerCase();

  const isWorker =
    role === "worker" ||
    role === "nurse" ||
    role === "caregiver" ||
    platformRole === "worker";

  if (!isWorker) return false;

  const registrationStatus = String(u.registrationStatus || "").toLowerCase();
  const workerStatus = String(u.workerStatus || "").toLowerCase();
  const status = String(u.status || "").toLowerCase();
  const onboardingStatus = String(u.onboardingStatus || "").toLowerCase();

  return (
    registrationStatus === "pending" ||
    registrationStatus === "registered" ||
    workerStatus === "registration_pending" ||
    status === "pending_registration_approval" ||
    (
      !registrationStatus &&
      !workerStatus &&
      status === "onboarding" &&
      onboardingStatus === "not_started"
    )
  );
});

async function handlePreviewInvoices() {
  try {
    setInvoiceBusy(true);
    setError("");

    if (!invoiceFacilityId || invoiceFacilityId === "all") {
      setError("Select a facility to preview invoices.");
      setInvoicePreview([]);
      return;
    }

    const eligibleShifts = await getEligibleShiftsForInvoice({
      tenantId: isTenantInternalAdmin ? profileTenantId : selectedTenant,
      facilityId: invoiceFacilityId,
      billingStart: new Date(invoiceStart),
      billingEnd: new Date(invoiceEnd),
    });

    const facility = facilities.find(
      (f) => String(f.id || "").toLowerCase() === String(invoiceFacilityId).toLowerCase()
    );

    const draft = buildInvoiceDraft({
      tenantId: isTenantInternalAdmin ? profileTenantId : selectedTenant,
      facilityId: invoiceFacilityId,
      facilityName: facility?.name || invoiceFacilityId,
      shifts: eligibleShifts,
      facilityRateMap: billingRates,
      workersByUid: usersByUid,
      adjustments: 0,
    });

    setInvoicePreview([draft]);
  } catch (err) {
    console.error(err);
    setError(err?.message || "Preview failed");
  } finally {
    setInvoiceBusy(false);
  }
}

useEffect(() => {
  let unsubscribeTenants = () => {};

  if (isTenantInternalAdmin && profileTenantId) {
    // Tenant Internal Admin:
    // subscribe only to their assigned tenant document.
    unsubscribeTenants = onSnapshot(
      doc(db, "tenants", profileTenantId),
      (tenantSnap) => {
        setTenantRecords(
          tenantSnap.exists()
            ? [
                {
                  id: tenantSnap.id,
                  tenantId: tenantSnap.id,
                  ...tenantSnap.data(),
                },
              ]
            : []
        );
      },
      (err) => {
        console.error(
          "TENANT DOCUMENT LISTENER ERROR",
          err
        );

        setError(
          `Tenant error: ${err?.code || ""} ${
            err?.message || ""
          }`
        );
      }
    );
  } else {
    // ShiftBridge Platform Admin:
    // subscribe to the complete tenant collection.
    unsubscribeTenants = onSnapshot(
      collection(db, "tenants"),
      (tenantSnap) => {
        setTenantRecords(
          tenantSnap.docs.map((tenantDoc) => ({
            id: tenantDoc.id,
            tenantId: tenantDoc.id,
            ...tenantDoc.data(),
          }))
        );
      },
      (err) => {
        console.error("TENANTS LISTENER ERROR", err);

        setError(
          `Tenants error: ${err?.code || ""} ${
            err?.message || ""
          }`
        );
      }
    );
  }

  return () => {
    unsubscribeTenants();
  };
}, [
  isTenantInternalAdmin,
  profileTenantId,
]);

const tenantOptions = useMemo(() => {
  const map = new Map();

  // The tenants collection is the primary source of truth.
  (tenantRecords || []).forEach((tenant) => {
    const tenantId = normalizeId(
      tenant?.tenantId || tenant?.id
    );

    if (!tenantId) return;

    map.set(tenantId, {
      ...tenant,
      id: tenant?.id || tenantId,
      tenantId,
      tenantName:
        tenant?.tenantName ||
        tenant?.name ||
        tenantId,
      tenantType:
        tenant?.tenantType ||
        "staffing_agency",
    });
  });

  // Legacy fallback for facilities whose tenant record
  // has not yet been created.
  (facilities || []).forEach((facility) => {
    const tenantId = normalizeId(facility?.tenantId);

    if (!tenantId || map.has(tenantId)) return;

    map.set(tenantId, {
      tenantId,
      tenantName:
        facility?.tenantName ||
        tenantId,
      tenantType:
        facility?.tenantType ||
        "staffing_agency",
      source: "facility_fallback",
    });
  });

  return Array.from(map.values()).sort((a, b) =>
    String(a?.tenantName || "").localeCompare(
      String(b?.tenantName || "")
    )
  );
}, [tenantRecords, facilities]);

const scopedFacilities = useMemo(() => {
  const rows = Array.isArray(facilities) ? facilities : [];

  let tenantRows = rows;

  if (selectedTenant !== "all") {
    tenantRows = tenantRows.filter(
      (facility) =>
        normalizeId(facility?.tenantId) === normalizeId(selectedTenant)
    );
  }

  return tenantRows.filter((facility) => {
    const facilityId = normalizeId(facility?.id || facility?.facilityId);
    const tenantId = normalizeId(facility?.tenantId);

    return canAccessFacility(profile, facilityId, tenantId);
  });
}, [facilities, profile, selectedTenant]);

const scopedShifts = useMemo(() => {
  const rows = Array.isArray(shifts) ? shifts : [];

  let tenantRows = rows;

  if (selectedTenant !== "all") {
    tenantRows = tenantRows.filter(
      (shift) => normalizeId(shift?.tenantId) === normalizeId(selectedTenant)
    );
  }

  return tenantRows.filter((shift) => {
    const facilityId = normalizeId(shift?.facilityId || shift?.facilityID);
    const tenantId = normalizeId(shift?.tenantId);

    return canAccessFacility(profile, facilityId, tenantId);
  });
}, [profile, shifts, selectedTenant]);

const shiftsFiltered = useMemo(() => {
  if (selectedFacility === "all") return scopedShifts;

  return scopedShifts.filter(
    (shift) =>
      normalizeId(shift?.facilityId || shift?.facilityID) ===
      normalizeId(selectedFacility)
  );
}, [selectedFacility, scopedShifts]);

const metrics = useMemo(() => {
  const canonical =
    buildShiftMetrics(
      shiftsFiltered
    );

  const total =
    Number(
      canonical.total || 0
    );

  const covered =
    Number(
      canonical.covered || 0
    );

  const coverage =
    total > 0
      ? Number(
          canonical.coverage || 0
        )
      : 0;

  const open =
    Number(
      canonical.open || 0
    );

  const offered =
    Number(
      canonical.offered || 0
    );

  const requested =
    Number(
      canonical.requested || 0
    );

  const timeRequired =
    Number(
      canonical.timeRequired || 0
    );

  const assigned =
    Number(
      canonical.assigned || 0
    );

  const acceptedAssigned =
    Number(
      canonical.acceptedAssigned || 0
    );

  const awaitingAcceptance =
    Number(
      canonical.awaitingAcceptance || 0
    );

  const activated =
    Number(
      canonical.activated || 0
    );

  const tracking =
    Number(
      canonical.tracking || 0
    );

  const arrived =
    Number(
      canonical.arrived || 0
    );

  const completed =
    Number(
      canonical.completed || 0
    );

  const openDemand =
    open +
    offered +
    requested +
    timeRequired +
    awaitingAcceptance;

  const active =
    acceptedAssigned +
    activated +
    tracking +
    arrived;

  const completedRows =
    Array.isArray(
      canonical.completedRows
    )
      ? canonical.completedRows
      : [];

  const completedHours =
    completedRows.reduce(
      (sum, shift) =>
        sum +
        shiftHours(shift),
      0
    );
const projectedRevenue = completedRows.reduce((sum, s) => {
  const rk = roleKey(
    s.role ||
      s.requiredPosition ||
      s.position ||
      s.workerType ||
      s.roleType
  );

  const fid = normalizeId(
    s.facilityId ||
      s.facilityID ||
      s.facility ||
      s.facilityProfileId
  );

  const facilityProfile = facilitiesById.get(fid) || {};
  const rates = facilityProfile?.rates || {};

  const rate = Number(
    s.billRate ||
      s.rates?.billRate ||
      s.rates?.[rk]?.billRate ||
      rates?.[rk]?.billRate ||
      billingRates?.[rk] ||
      0
  );
console.log("REVENUE DEBUG", {
  shiftId: s.id,
  facilityId: fid,
  role: rk,
  billRate: rate,
  hours: shiftHours(s),
  revenue: shiftHours(s) * rate,
  facilityRates: rates,
});


  return sum + shiftHours(s) * rate;
}, 0);

   return {
    total,
    covered,
    coverage,

    open,
    offered,
    requested,
    timeRequired,
    openDemand,

    assigned,
    acceptedAssigned,
    awaitingAcceptance,

    activated,
    tracking,
    arrived,
    active,

    completed,
    completedHours,
    projectedRevenue,
  };

}, [billingRates, shiftsFiltered, facilitiesById]);

const facilityStats = useMemo(() => {
  const selectedFacilityId = normalizeId(selectedFacility);

  return (scopedFacilities || [])
    .filter((facility) => {
      if (selectedFacility === "all") return true;

      const facilityId = normalizeId(
        facility?.id || facility?.facilityId
      );

      return facilityId === selectedFacilityId;
    })
    .map((facility) => {
      const fid = normalizeId(
        facility?.id || facility?.facilityId
      );

      const rows = (scopedShifts || []).filter(
        (shift) =>
          normalizeId(
            shift?.facilityId || shift?.facilityID
          ) === fid
      );

      const facilityMetrics =
        buildShiftMetrics(
          rows
        );

      const total =
        Number(
          facilityMetrics.total || 0
        );

      const completed =
        Number(
          facilityMetrics.completed || 0
        );

      const open =
        Number(
          facilityMetrics.open || 0
        ) +
        Number(
          facilityMetrics.offered || 0
        ) +
        Number(
          facilityMetrics.requested || 0
        ) +
        Number(
          facilityMetrics.timeRequired || 0
        ) +
        Number(
          facilityMetrics.awaitingAcceptance || 0
        );

            const late = rows.filter((shift) => {
        const operational =
          getShiftOperationalSummary(
            shift
          );

        const status =
          operational.status;

        const startTime =
          toMillis(
            shift?.startTime
          );

        return (
          status !== "arrived" &&
          status !== "completed" &&
          Number.isFinite(startTime) &&
          startTime < Date.now()
        );
      }).length;

            const fillRate =
        total > 0
          ? Number(
              facilityMetrics.coverage ||
                0
            )
          : 0;

      return {
        fid,
        name:
          facility?.name ||
          facility?.facilityName ||
          fid,
        total,
        completed,
        open,
        late,
        fillRate,
      };
    });
}, [
  scopedFacilities,
  scopedShifts,
  selectedFacility,
]);

const recentShifts = useMemo(() => {
  return (shiftsFiltered || []).slice(0, 12);
}, [shiftsFiltered]);

const scopedUsers = useMemo(() => {
  const rows = Array.isArray(users) ? users : [];
  const selectedTenantId = normalizeId(selectedTenant);

  return rows.filter((user) => {
    const primaryFacilityId = normalizeId(
      user?.facilityId ||
        user?.primaryFacilityId ||
        user?.assignedFacilityId ||
        ""
    );

    const facilityIds = [
      primaryFacilityId,

      ...(Array.isArray(user?.facilityIds)
        ? user.facilityIds.map(normalizeId)
        : []),

      ...(Array.isArray(user?.allowedFacilityIds)
        ? user.allowedFacilityIds.map(normalizeId)
        : []),

      ...(Array.isArray(user?.assignedFacilityIds)
        ? user.assignedFacilityIds.map(normalizeId)
        : []),
    ].filter(
      (value, index, array) =>
        value &&
        value !== "all" &&
        array.indexOf(value) === index
    );

    const tenantIds = [
      normalizeId(user?.tenantId),
      normalizeId(user?.tenantKey),

      ...facilityIds.map((facilityId) => {
        const facility =
          facilitiesById.get(facilityId) || {};

        return normalizeId(
          facility?.tenantId ||
            facility?.tenantKey ||
            ""
        );
      }),
    ].filter(
      (value, index, array) =>
        value &&
        value !== "all" &&
        array.indexOf(value) === index
    );

    if (
      selectedTenantId &&
      selectedTenantId !== "all" &&
      !tenantIds.includes(selectedTenantId)
    ) {
      return false;
    }

    const accessTenantId =
      selectedTenantId &&
      selectedTenantId !== "all"
        ? selectedTenantId
        : tenantIds[0] || "";

    if (
      accessTenantId &&
      canAccessTenant(profile, accessTenantId)
    ) {
      return true;
    }

    return facilityIds.some((facilityId) =>
      canAccessFacility(
        profile,
        facilityId,
        accessTenantId
      )
    );
  });
}, [
  profile,
  users,
  selectedTenant,
  facilitiesById,
]);

const workforceCounts = useMemo(() => {
  const rows = scopedUsers || [];

  const total = rows.length;

  const active = rows.filter(
    (u) => String(u.status || "").toLowerCase() === "active"
  ).length;

  const pending = rows.filter(
    (u) => String(u.status || "").toLowerCase() === "pending"
  ).length;

  const admins = rows.filter((u) =>
    String(u.role || u.platformRole || "").toLowerCase().includes("admin")
  ).length;

  const planners = rows.filter((u) =>
    String(u.role || u.platformRole || "").toLowerCase().includes("planner")
  ).length;

  const workers = rows.filter(
    (u) => String(u.role || u.platformRole || "").toLowerCase() === "worker"
  ).length;

  return {
    total,
    active,
    pending,
    admins,
    planners,
    workers,
  };
}, [scopedUsers]);

const filteredAdminLogs = useMemo(() => {
  if (!canViewAdminLogs) return [];

  const q = String(activitySearch || "").trim().toLowerCase();
  const days = Number(activityDays || 0);
  const now = Date.now();

  return (adminLogs || []).filter((log) => {
    const tenantOk =
      selectedTenant === "all" ||
      String(log.tenantId || "").toLowerCase() ===
      String(selectedTenant).toLowerCase();
    const action = String(log.action || "").toLowerCase();
    const targetEmail = String(log.targetEmail || "").toLowerCase();
    const targetUid = String(log.targetUid || "").toLowerCase();
    const performedBy = String(log.performedBy || "").toLowerCase();
    const createdMs = toMillis(log.createdAt);
    
    if (!tenantOk) return false;

    if (activityActionFilter !== "all" && action !== activityActionFilter) {
      return false;
    }

    if (q) {
      const hay = `${action} ${targetEmail} ${targetUid} ${performedBy}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }

    if (days > 0 && Number.isFinite(createdMs)) {
      const ageMs = now - createdMs;
      if (ageMs > days * 86400000) return false;
    }

    return true;
  });

}, [
  adminLogs,
  activitySearch,
  activityActionFilter,
  activityDays,
  canViewAdminLogs,
  selectedTenant,
]);

async function markInvoiceStatus(invoiceId, status) {
  if (!canGenerateInvoices && !canMarkInvoicesPaid && !canVoidInvoices) {
    setError("You do not have permission to update invoices.");
    return;
  }

  try {
    await updateDoc(doc(db, "invoices", invoiceId), {
      status,
      updatedAt: serverTimestamp(),
    });
  } catch (err) {
    setError(err?.message || "Failed to update invoice");
  }
}

  function buildInvoiceDoc(inv) {
    const docPdf = new jsPDF();

    const invoiceNumber =
      inv.invoiceNumber ||
      inv.number ||
      `INV-${String(inv.id || "").slice(0, 8).toUpperCase() || "DRAFT"}`;

    const invoiceDate =
      inv.invoiceDate ||
      inv.createdAt ||
      inv.createdOn ||
      Date.now();

    const dueDate =
      inv.dueDate ||
      (() => {
        const base = toMillis(inv.invoiceDate || inv.createdAt || Date.now());
        const d = new Date(base);
        d.setDate(d.getDate() + 14);
        return d;
      })();

    const billingStart = inv.weekStart || inv.billingStart || inv.startDate || null;
    const billingEnd = inv.weekEnd || inv.billingEnd || inv.endDate || null;

    const lines = Array.isArray(inv.lines)
      ? inv.lines
      : Array.isArray(inv.lineItems)
        ? inv.lineItems
        : [];

    const totalHours =
      Number(inv.totalHours || 0) ||
      lines.reduce((sum, line) => sum + Number(line.hours || 0), 0);

    const totalAmount =
      Number(inv.totalAmount ?? inv.total ?? 0) ||
      lines.reduce((sum, line) => sum + Number(line.lineTotal || line.amount || 0), 0);

    const remitToRiviera =
      (String(inv.remitTo || "").toLowerCase() === "riviera") ||
      (
        String(inv.factoringCompany || "").toLowerCase().includes("riviera") &&
        String(inv.factoringStatus || "").toLowerCase() === "submitted"
      );

    // Header
    docPdf.setFontSize(20);
    docPdf.setTextColor(15, 23, 42);
    docPdf.text("DogeBridge Staffing Invoice", 14, 18);

    docPdf.setFontSize(9);
    docPdf.setTextColor(100, 116, 139);
    docPdf.text("Prepared through ShiftBridge Enterprise", 14, 24);

    docPdf.setDrawColor(226, 232, 240);
    docPdf.line(14, 28, 196, 28);

    // Invoice details
    docPdf.setFontSize(11);
    docPdf.setTextColor(15, 23, 42);
    docPdf.text(`Facility: ${inv.facilityName || inv.facilityId || "—"}`, 14, 38);
    docPdf.text(`Invoice Number: ${invoiceNumber}`, 14, 45);
    docPdf.text(`Invoice Date: ${fmtDate(invoiceDate)}`, 14, 52);
    docPdf.text(`Due Date: ${fmtDate(dueDate)}`, 14, 59);
    docPdf.text(
      `Billing Period: ${fmtDate(billingStart)} to ${fmtDate(billingEnd)}`,
      14,
      66
    );
    docPdf.text(`Status: ${String(inv.status || "draft").toUpperCase()}`, 14, 73);

    // Service lines
    autoTable(docPdf, {
      startY: 82,
      head: [["Date", "Role", "Coverage", "Hours", "Rate", "Amount"]],
      body: lines.map((line) => [
        fmtDate(line.startTime || line.date),
        niceRole(line.role),
        line.startTime && line.endTime
          ? `${fmtTime(line.startTime)} - ${fmtTime(line.endTime)}`
          : "—",
        Number(line.hours || 0).toFixed(2),
        fmtMoney(line.billRate || line.rate || 0),
        fmtMoney(line.lineTotal || line.amount || 0),
      ]),
      styles: {
        fontSize: 9,
        cellPadding: 4,
        textColor: [31, 41, 55],
        lineColor: [226, 232, 240],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: 255,
        fontStyle: "bold",
      },
      bodyStyles: {
        valign: "middle",
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252],
      },
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 30 },
        2: { cellWidth: 42 },
        3: { halign: "right", cellWidth: 20 },
        4: { halign: "right", cellWidth: 28 },
        5: { halign: "right", cellWidth: 30 },
      },
      margin: { left: 14, right: 14 },
    });

    const finalY = docPdf.lastAutoTable?.finalY || 96;

    // Totals
    docPdf.setDrawColor(226, 232, 240);
    docPdf.line(14, finalY + 6, 196, finalY + 6);

    docPdf.setFontSize(12);
    docPdf.setTextColor(15, 23, 42);
    docPdf.text(`Total Hours: ${Number(totalHours).toFixed(2)} hrs`, 14, finalY + 16);
    docPdf.text(`Total Amount: ${fmtMoney(totalAmount)}`, 14, finalY + 24);

    // Footer / remittance
    docPdf.setFontSize(9);
    docPdf.setTextColor(100, 116, 139);
    docPdf.text("Issued by DogeBridge Staffing", 14, finalY + 36);
    docPdf.text("Operational support provided through ShiftBridge Enterprise", 14, finalY + 42);

    docPdf.setTextColor(15, 23, 42);
    docPdf.setFontSize(10);
    docPdf.text("Remit To:", 14, finalY + 52);

    docPdf.setFontSize(9);

    if (remitToRiviera) {
      docPdf.text("Riviera Finance", 14, finalY + 58);
      docPdf.text(
        "This receivable has been assigned to Riviera Finance. Please remit payment",
        14,
        finalY + 64
      );
      docPdf.text(
        "according to Riviera Finance remittance instructions and reference this invoice number.",
        14,
        finalY + 70
      );
    } else {
      docPdf.text("DogeBridge Staffing", 14, finalY + 58);
      docPdf.text(
        "Please remit payment to DogeBridge Staffing and reference this invoice number.",
        14,
        finalY + 64
      );
    }

    return docPdf;
  }

  async function generateSingleInvoice(inv) {
    if (!canGenerateInvoices) {
      setError("You do not have permission to generate invoices.");
      return;
    }

    try {
      setSending(true);
      setError("");

      const weekStart = invoiceDateFrom;
      const weekEnd = invoiceDateTo;

      const nowMs = Date.now();
      const invoiceDate = new Date(nowMs);
      const dueDate = new Date(nowMs);
      dueDate.setDate(dueDate.getDate() + 14);

      const invoiceNumber = `INV-${String(inv.facilityId || "FAC")
        .replace(/[^a-z0-9]/gi, "")
        .toUpperCase()
        .slice(0, 6)}-${String(nowMs).slice(-6)}`;

      const lines = (inv.shifts || []).map((s) => {
        const workerUid = getAssignedUid(s) || "";
        const worker = usersByUid.get(workerUid);

        const hours = Number(s.computedHours || shiftHours(s) || 0);
        const facilityProfile =
  facilitiesById.get(
    String(inv.facilityId || "").toLowerCase()
  ) || {};

const rates =
  facilityProfile?.rates || {};

const rk = roleKey(
  s.role ||
  s.requiredPosition ||
  s.position ||
  s.workerType ||
  s.roleType
);

const rate = Number(
  s.computedBillRate ||
  s.billRate ||
  rates?.[rk]?.billRate ||
  0
);
        const amount = Number((s.lineTotal || hours * rate).toFixed(2));

        return {
          shiftId: s.id,
          date: s.startTime || null,
          startTime: s.startTime || null,
          endTime: s.endTime || null,
          role: s.role || "",
          workerUid,
          workerName:
            worker?.displayName ||
            worker?.fullName ||
            worker?.name ||
            worker?.email ||
            workerUid ||
            "—",
          workerEmail: worker?.email || "",
          hours: Number(hours.toFixed(2)),
          billRate: rate,
          lineTotal: amount,
        };
      });

      const totalHours = Number(
        lines.reduce((sum, line) => sum + Number(line.hours || 0), 0).toFixed(2)
      );

      const totalAmount = Number(
        lines.reduce((sum, line) => sum + Number(line.lineTotal || 0), 0).toFixed(2)
      );

      const fundingCompany =
  FACTORING_COMPANIES.find((c) => c.id === selectedFundingCompany) ||
  FACTORING_COMPANIES[0];

      const invoiceTenantId = normalizeId((facilities || []).find(f => normalizeId(f.facilityId || f.id) === normalizeId(inv.facilityId))?.tenantId);
      if (!invoiceTenantId || invoiceTenantId === "all" || (isTenantInternalAdmin && invoiceTenantId !== profileTenantId)) throw new Error("Invoice tenant scope is invalid.");
      const docRef = await addDoc(collection(db, "invoices"), {
        tenantId: invoiceTenantId,
        invoiceNumber,
        invoiceDate,
        dueDate,
        facilityId: String(inv.facilityId || "").toLowerCase(),
        facilityName: inv.facilityName || inv.facilityId || "—",
        weekStart,
        weekEnd,
        billingStart: weekStart,
        billingEnd: weekEnd,
        lines,
        totalHours,
        totalAmount,
        status: "draft",

       factoringCompany: fundingCompany.name,
fundingCompany: fundingCompany.id,
fundingAdvanceRate: fundingCompany.defaultAdvanceRate,
fundingFeePercent: fundingCompany.defaultFeePercent,
factoringStatus: "not_submitted",
fundingStatus: "not_submitted",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || "admin",
      });

      alert(`Invoice created: ${invoiceNumber}`);

      for (const s of inv.shifts || []) {
        await updateDoc(doc(db, "shifts", s.id), {
          invoiceId: docRef.id,
          invoiceNumber,
          invoiceStatus: "billed",
          updatedAt: serverTimestamp(),
        });
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || "Invoice generation failed");
    } finally {
      setSending(false);
    }
  }

  async function emailInvoice(inv) {
    try {
      const remitToRiviera =
        String(inv.remitTo || "").toLowerCase() === "riviera" ||
        (
          String(inv.factoringCompany || "").toLowerCase().includes("riviera") &&
          String(inv.factoringStatus || "").toLowerCase() === "submitted"
        );

      const subject = `Invoice ${inv.invoiceNumber || inv.id} - DogeBridge Staffing - ${inv.facilityName || inv.facilityId || "Facility"
        }`;

      const body =
        `Dear Facility Team,\n\n` +
        `Please find the attached staffing invoice for the billing period ` +
        `${fmtDate(inv.weekStart || inv.billingStart)} to ${fmtDate(
          inv.weekEnd || inv.billingEnd
        )}.\n\n` +
        `This invoice is issued by DogeBridge Staffing and was prepared through our ShiftBridge Enterprise operational platform.\n` +
        (
          remitToRiviera
            ? `This receivable has been assigned to Riviera Finance. Please remit payment according to Riviera remittance instructions and reference the invoice number below.\n\n`
            : `Please remit payment to DogeBridge Staffing and reference the invoice number below.\n\n`
        ) +
        `Invoice Number: ${inv.invoiceNumber || inv.id}\n` +
        `Invoice Date: ${fmtDate(inv.invoiceDate || inv.createdAt)}\n` +
        `Due Date: ${fmtDate(inv.dueDate)}\n` +
        `Total Amount: ${fmtMoney(inv.totalAmount ?? inv.total ?? 0)}\n\n` +
        `Please contact us if you need any clarification.\n\n` +
        `Regards,\n` +
        `DogeBridge Staffing`;

      await updateDoc(doc(db, "invoices", inv.id), {
        status: "emailed",
        emailedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        emailSubject: subject,
        emailBody: body,
      });

      window.alert(
        `Invoice marked as emailed for ${inv.facilityName || inv.facilityId}.\n\n` +
        `Suggested subject:\n${subject}\n\n` +
        `Remit To:\n${remitToRiviera ? "Riviera Finance" : "DogeBridge Staffing"}`
      );
    } catch (err) {
      setError(err?.message || "Failed to mark invoice as emailed");
    }
  }

async function markFundingFunded(inv) {
  try {
    await updateDoc(doc(db, "invoices", inv.id), {
      fundingStatus: "funded",
      factoringStatus: "funded",
      fundedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    alert("Funding marked as funded.");
  } catch (err) {
    setError(err?.message || "Failed to mark funding as funded.");
  }
}

async function markFundingCollected(inv) {
  try {
    await updateDoc(doc(db, "invoices", inv.id), {
      fundingStatus: "collected",
      factoringStatus: "collected",
      collectedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    alert("Funding marked as collected.");
  } catch (err) {
    setError(err?.message || "Failed to mark funding as collected.");
  }
}

async function submitInvoiceForFunding(inv) {
  try {
    await updateDoc(doc(db, "invoices", inv.id), {
      submittedForFundingAt: serverTimestamp(),
      factoringStatus: "submitted",
      fundingStatus: "submitted",
      fundingCompany: selectedFundingCompany,
      updatedAt: serverTimestamp(),
    });

    window.alert(
      `Invoice submitted for funding for ${inv.facilityName || inv.facilityId}.`
    );
  } catch (err) {
    setError(err?.message || "Failed to submit invoice for funding");
  }
}

  function exportInvoicePdf(inv) {
    try {
      const pdf = buildInvoiceDoc(inv);
      const facilitySlug = String(inv.facilityName || inv.facilityId || "invoice")
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_-]/g, "");
      const invoiceNo = String(inv.invoiceNumber || inv.id || "draft").replace(/[^\w-]/g, "_");
      pdf.save(`invoice_${facilitySlug}_${invoiceNo}.pdf`);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to export invoice PDF");
    }
  }

  async function archiveTestShifts() {
  const confirmArchive = window.confirm(
    "Archive old completed/test shifts? This will remove them from payroll testing without deleting them."
  );

  if (!confirmArchive) return;

  const oldTestShiftIds = [
    "x4SsjiUsmnS1fA2LW2zy",
    "0byPthPjHHtO8FGUfSsz",
  ];

  try {
    await Promise.all(
      oldTestShiftIds.map((shiftId) =>
        updateDoc(doc(db, "shifts", shiftId), {
          status: "archived",
          lifecycleStatus: "archived",
          archived: true,
          testData: true,
          completed: false,
          updatedAt: serverTimestamp(),
        })
      )
    );

    alert("Old test shifts archived.");
  } catch (err) {
    console.error("archiveTestShifts error:", err);
    alert(err?.message || "Failed to archive test shifts.");
  }
}

  async function handleSignOut() {
    try {
      await signOut(auth);
    } catch (err) {
      setError(err?.message || "Failed to sign out");
    }
  }

  const layoutWide = viewportWidth >= 1100;
  const layoutMid = viewportWidth >= 800;








async function approveInvitedUser(userId) {
  try {
    const invitedUser =
      invitedUsers.find(
        (u) =>
          String(u.uid || u.id || "").toLowerCase() ===
          String(userId || "").toLowerCase()
      ) || {};

    const tenantId =
  normalizeId(invitedUser.tenantId) ||
  (selectedTenant !== "all"
    ? normalizeId(selectedTenant)
    : normalizeId(profile?.tenantId));

    const facilityId =
  normalizeId(invitedUser.facilityId) ||
  (selectedFacility !== "all"
    ? normalizeId(selectedFacility)
    : "");
    const email = String(invitedUser.email || "").trim().toLowerCase();

    if (!email) {
      alert("Invite record is missing email.");
      return;
    }

    const approvedBy = auth.currentUser?.uid || "admin";

    await setDoc(
  doc(db, "users", userId),
  {
    uid: userId,
    email,
    displayName: invitedUser.displayName || "",
    role: invitedUser.role || "worker",
    platformRole: invitedUser.platformRole || "worker",

    tenantId,
    facilityId,
    facilityIds: facilityId ? [facilityId] : [],

    status: "pending_registration_approval",
    registrationStatus: "pending",
    workerStatus: "registration_pending",
    invitationStatus: "registered",
    onboardingStatus: "locked",
    complianceStatus: "not_started",
    canAccessOnboarding: false,

    workerOperationalState: "REGISTRATION_PENDING",

    adminApproved: false,
    createdFromInvite: true,

    registeredAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  },
  { merge: true }
);

    await setDoc(
      doc(db, "invites", userId),
      {
        uid: userId,
        email,
        tenantId,
        facilityId,
        role: invitedUser.role || "worker",
        platformRole: invitedUser.platformRole || "worker",

        inviteStatus: "registered",
        invitationStatus: "registered",
        status: "registered",

        registrationStatus: "pending",
        onboardingStatus: "locked",
        workerStatus: "registration_pending",
        complianceStatus: "not_started",
        canAccessOnboarding: false,

        registeredAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    await addDoc(collection(db, "adminLogs"), {
      action: "worker_registered_pending_approval",
      targetUid: userId,
      targetEmail: email,
      tenantId,
      facilityId,
      performedBy: auth.currentUser?.email || approvedBy,
      createdAt: serverTimestamp(),
    });

setInviteRecords((prev) =>
  (prev || []).filter((u) => {
    const inviteUid = String(u.uid || u.id || "")
      .trim()
      .toLowerCase();

    const inviteEmail = String(u.email || "")
      .trim()
      .toLowerCase();

    return (
      inviteUid !== String(userId || "")
        .trim()
        .toLowerCase() &&
      inviteEmail !== String(email || "")
        .trim()
        .toLowerCase()
    );
  })
);

    alert("Invite approved. Worker can now complete onboarding.");
  } catch (err) {
    console.error("approveInvitedUser error:", err);
    alert(err?.message || "Unable to approve invited user.");
  }
}

async function handleSaveFacilityProfile() {
  console.log("Saving facility:", editingFacility);

  try {
    const tenantId = normalizeId(
      editingFacility?.tenantId ||
        selectedTenant
    );

    if (!tenantId || tenantId === "all") {
      alert("A specific tenant is required.");
      return;
    }

    const facilityName = String(
      editingFacility?.name ||
        editingFacility?.facilityName ||
        ""
    ).trim();

    if (!facilityName) {
      alert("Enter facility name before saving.");
      return;
    }

    const facilitySlug = normalizeId(facilityName);

    const existingFacilityId = normalizeId(
      editingFacility?.id ||
        editingFacility?.facilityId ||
        ""
    );

    const cleanFacilityId = creatingFacility
      ? `${tenantId}__${facilitySlug}`
      : existingFacilityId ||
        `${tenantId}__${facilitySlug}`;

    const tenantRecord = (tenantOptions || []).find(
      (tenant) =>
        normalizeId(tenant?.tenantId) === tenantId
    );

    const facilityRef = doc(
      db,
      "facilityProfiles",
      cleanFacilityId
    );

    if (creatingFacility) {
      const existingSnap = await getDoc(facilityRef);

      if (existingSnap.exists()) {
        alert(
          "A facility with this name already exists for this tenant."
        );
        return;
      }
    }

    const payload = {
      ...editingFacility,

      id: cleanFacilityId,
      facilityId: cleanFacilityId,

      name: facilityName,
      facilityName,

      tenantId,
      tenantName:
        tenantRecord?.tenantName ||
        editingFacility?.tenantName ||
        tenantId,

      tenantType:
        tenantRecord?.tenantType ||
        editingFacility?.tenantType ||
        "staffing_agency",

   updatedAt: serverTimestamp(),

updatedBy:
  auth.currentUser?.uid ||
  "platform_admin",

updatedByEmail:
  auth.currentUser?.email ||
  profile?.email ||
  "",

...(creatingFacility
  ? {
      createdAt: serverTimestamp(),
      createdBy:
        auth.currentUser?.uid ||
        "platform_admin",
      createdByEmail:
        auth.currentUser?.email ||
        profile?.email ||
        "",
      source: "tenant_facility_creation",
    }
  : {}),
    };

    await saveFacilityFinancialProfile(cleanFacilityId, payload);

    setFacilities((previous) => {
      const rows = Array.isArray(previous)
        ? previous
        : [];

      const existingIndex = rows.findIndex(
        (facility) =>
          normalizeId(
            facility?.id ||
              facility?.facilityId
          ) === cleanFacilityId
      );

      if (existingIndex === -1) {
        return [...rows, payload];
      }

      return rows.map((facility, index) =>
        index === existingIndex
          ? {
              ...facility,
              ...payload,
            }
          : facility
      );
    });

    setSelectedFacility(cleanFacilityId);
    setCreatingFacility(false);
    setEditingFacility(null);

    alert(
      creatingFacility
        ? "Facility created."
        : "Facility profile updated."
    );
  } catch (err) {
    console.error(
      "handleSaveFacilityProfile error:",
      err
    );

    setError(
      err?.message ||
        "Failed to save facility profile."
    );
  }
}



function generateUniqueTenantId(tenantName) {
  const baseTenantId = String(tenantName || "")
    .trim()
    .toLowerCase()
    .replace(
      /\b(llc|incorporated|inc|ltd|limited|corp|corporation|pllc|lp)\b\.?/g,
      ""
    )
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);

  if (!baseTenantId) return "";

  const existingTenantIds = new Set(
    (tenantOptions || [])
      .map((tenant) =>
        String(tenant?.tenantId || tenant?.id || "")
          .trim()
          .toLowerCase()
      )
      .filter(Boolean)
  );

  if (!existingTenantIds.has(baseTenantId)) {
    return baseTenantId;
  }

  let suffix = 2;
  let tenantId = `${baseTenantId}_${suffix}`;

  while (existingTenantIds.has(tenantId)) {
    suffix += 1;
    tenantId = `${baseTenantId}_${suffix}`;
  }

  return tenantId;
}

function openNewTenantEditor() {
  setError("");

  setEditingTenant({
    isNew: true,

    id: "",
    tenantId: "",
    tenantName: "",

    tenantType: "staffing_agency",
    billingModel: "agency_billing",
    payrollModel: "agency_managed",

    status: "onboarding",
    subscriptionStatus: "pending_activation",
    onboardingStatus: "tenant_created",

    primaryContactName: "",
    primaryContactEmail: "",
    primaryContactPhone: "",

    billingContactName: "",
    billingContactEmail: "",

    address: "",
    city: "",
    state: "TX",
    zip: "",
    country: "USA",

    phone: "",
    email: "",
    website: "",

    displayName: "",
    logoUrl: "",
    primaryColor: "#2563eb",
  });
}

function openTenantEditor(tenantId) {
  if (!tenantId || tenantId === "all") {
    setError("Select a tenant first.");
    return;
  }

  const cleanTenantId = String(tenantId || "").trim().toLowerCase();

  const tenant =
    (tenantOptions || []).find(
      (t) =>
        String(t.tenantId || t.id || "").toLowerCase() === cleanTenantId
    ) || {};

const tenantAddress =
  tenant?.address &&
  typeof tenant.address === "object"
    ? tenant.address
    : {};

const tenantBranding =
  tenant?.branding &&
  typeof tenant.branding === "object"
    ? tenant.branding
    : {};

const tenantIsActive =
  String(tenant?.status || "").toLowerCase() === "active";

setEditingTenant({
  isNew: false,

  id: cleanTenantId,
  tenantId: cleanTenantId,

  tenantName:
    tenant?.tenantName ||
    tenant?.name ||
    "",

  displayName:
    tenantBranding?.displayName ||
    tenant?.displayName ||
    tenant?.tenantName ||
    "",

  tenantType:
    tenant?.tenantType ||
    "staffing_agency",

  billingModel:
    tenant?.billingModel ||
    "agency_billing",

  payrollModel:
    tenant?.payrollModel ||
    "agency_managed",

  status:
    tenant?.status ||
    "active",

  subscriptionStatus:
    tenant?.subscriptionStatus ||
    (tenantIsActive ? "active" : "pending_activation"),

  onboardingStatus:
    tenant?.onboardingStatus ||
    (tenantIsActive ? "completed" : "tenant_created"),

  primaryContactName:
    tenant?.primaryContactName ||
    tenant?.contactName ||
    "",

  primaryContactEmail:
    tenant?.primaryContactEmail ||
    tenant?.contactEmail ||
    tenant?.email ||
    "",

  primaryContactPhone:
    tenant?.primaryContactPhone ||
    tenant?.contactPhone ||
    tenant?.phone ||
    "",

  billingContactName:
    tenant?.billingContactName ||
    "",

  billingContactEmail:
    tenant?.billingContactEmail ||
    "",

  address:
    typeof tenant?.address === "string"
      ? tenant.address
      : tenantAddress?.street || "",

  city:
    tenant?.city ||
    tenantAddress?.city ||
    "",

  state:
    tenant?.state ||
    tenantAddress?.state ||
    "TX",

  zip:
    tenant?.zip ||
    tenant?.postalCode ||
    tenantAddress?.postalCode ||
    "",

  country:
    tenant?.country ||
    tenantAddress?.country ||
    "USA",

  phone:
    tenant?.phone ||
    "",

  email:
    tenant?.email ||
    "",

  website:
    tenant?.website ||
    "",

  logoUrl:
    tenantBranding?.logoUrl ||
    tenant?.logoUrl ||
    "",

  primaryColor:
    tenantBranding?.primaryColor ||
    tenant?.primaryColor ||
    "#2563eb",
});
}

async function handleSaveTenantProfile() {
  console.log("TENANT SAVE CLICKED", editingTenant);

  const tenantName = String(
  editingTenant?.tenantName || ""
).trim();

const isNewTenant = editingTenant?.isNew === true;

if (!tenantName) {
  alert("Tenant name is required.");
  return;
}

const cleanTenantId = isNewTenant
  ? generateUniqueTenantId(tenantName)
  : String(
      editingTenant?.tenantId ||
        editingTenant?.id ||
        selectedTenant ||
        ""
    )
      .trim()
      .toLowerCase();

if (!cleanTenantId || cleanTenantId === "all") {
  alert("ShiftBridge could not generate a valid tenant ID.");
  return;
}

  try {
    const payload = {
      id: cleanTenantId,
      tenantId: cleanTenantId,
      tenantName: editingTenant?.tenantName || "",
      tenantType: editingTenant?.tenantType || "staffing_agency",
      billingModel:
        editingTenant?.billingModel || "agency_billing",

      payrollModel:
        editingTenant?.payrollModel || "agency_managed",

      status:
        editingTenant?.status ||
        (isNewTenant ? "onboarding" : "active"),

      subscriptionStatus:
        editingTenant?.subscriptionStatus ||
        (isNewTenant ? "pending_activation" : "active"),

      onboardingStatus:
        editingTenant?.onboardingStatus ||
        (isNewTenant ? "tenant_created" : "completed"),

      address: editingTenant?.address || "",
      city: editingTenant?.city || "",
      state: editingTenant?.state || "",
      zip: editingTenant?.zip || "",

      phone: editingTenant?.phone || "",
      email: editingTenant?.email || "",
      website: editingTenant?.website || "",

      primaryContactName:
        editingTenant?.primaryContactName || "",

      primaryContactEmail:
        editingTenant?.primaryContactEmail || "",

      primaryContactPhone:
        editingTenant?.primaryContactPhone || "",

      billingContactName:
        editingTenant?.billingContactName || "",

      billingContactEmail:
        editingTenant?.billingContactEmail || "",

      country: editingTenant?.country || "USA",

      branding: {
        displayName:
          editingTenant?.displayName ||
          editingTenant?.tenantName ||
          "",
        logoUrl: editingTenant?.logoUrl || "",
        primaryColor:
          editingTenant?.primaryColor || "#2563eb",
      },

      ...(isNewTenant
        ? {
          createdAt: serverTimestamp(),
          source: "platform_tenant_creation",
        }
        : {}),

      updatedAt: serverTimestamp(),
    };

    await setDoc(doc(db, "tenants", cleanTenantId), payload, { merge: true });

    setSelectedTenant(cleanTenantId);
    setEditingTenant(null);

    alert("Tenant saved. Refresh if the dropdown does not update immediately.");
  } catch (err) {
    console.error("handleSaveTenantProfile error:", err);
    alert(err?.message || "Unable to save tenant.");
  }
}

const pendingApprovalCount = (users || []).filter(
  (u) =>
    u.status === "pending" ||
    u.status === "onboarding" ||
    u.onboardingStatus === "submitted"
).length;

const unpaidInvoiceCount = (invoices || []).filter(
  (inv) =>
    inv.status === "draft" ||
    inv.status === "sent" ||
    inv.status === "unpaid" ||
    inv.status === "overdue"
).length;

const alerts = useMemo(() => {
  return (shifts || [])
    .filter((s) => {
      const tenantOk =
  selectedTenant === "all" ||
  String(s.tenantId || "").toLowerCase() ===
    String(selectedTenant).toLowerCase();

if (!tenantOk) return false;
const operational =
  getShiftOperationalSummary(
    s
  );

const status =
  operational.status;

const start =
  toMillis(
    s.startTime
  );

if (
  !Number.isFinite(start)
) {
  return false;
}

const minsToStart =
  Math.floor(
    (start - Date.now()) /
      60000
  );

/*
 * Uncovered demand approaching
 * start time.
 */
if (
  [
    "open",
    "offered",
    "requested",
    "time_required",
  ].includes(status) &&
  minsToStart <= 120
) {
  return true;
}

/*
 * A worker has been selected but
 * has not committed yet.
 */
if (
  operational
    .awaitingAcceptance &&
  minsToStart <= 30
) {
  return true;
}

/*
 * Worker accepted but has still
 * not activated near start.
 */
if (
  status === "assigned" &&
  operational.accepted &&
  minsToStart <= 30
) {
  return true;
}

/*
 * Once the shift starts, anything
 * short of arrival/completion is
 * an operational arrival risk.
 */
if (
  ![
    "arrived",
    "completed",
  ].includes(status) &&
  minsToStart < 0
) {
  return true;
}

return false;
    })
    .slice(0, 20);
}, [shifts, selectedTenant]);

const activeAlertCount = (alerts || []).filter(
  (a) =>
    a.status !== "resolved" &&
    a.status !== "closed"
).length;

const pendingPayrollCount = 0;

function goToAttention(tabName) {
  setAttentionTarget(tabName);
 setActiveTab(tabName);

  setTimeout(() => {
    const el = document.getElementById(`${tabName}-attention`);

    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, 180);
}

const handleCreateInternalAdminInvite = async () => {
  setSavingInternalAdminInvite(true);
  setInternalAdminInviteMessage("");

  try {
    const name = String(
      internalAdminInviteForm.name || ""
    ).trim();

    const emailLower = String(
      internalAdminInviteForm.email || ""
    )
      .trim()
      .toLowerCase();

    const phone = String(
      internalAdminInviteForm.phone || ""
    ).trim();

    const role = String(
      internalAdminInviteForm.role || ""
    )
      .trim()
      .toLowerCase();

const accessScope = String(
  internalAdminInviteForm.accessScope ||
    "all_facilities"
)
  .trim()
  .toLowerCase();

const allowedFacilityIds =
  accessScope ===
  "selected_facilities"
    ? Array.from(
        new Set(
          (
            internalAdminInviteForm.allowedFacilityIds ||
            []
          )
            .map(normalizeId)
            .filter(Boolean)
            .filter(
              (facilityId) =>
                facilityId !== "all"
            )
        )
      )
    : [];


/* =========================================================
   INTERNAL ADMIN SCOPE VALIDATION
   ========================================================= */

if (!name) {
  throw new Error(
    "Administrator name is required."
  );
}

if (!emailLower) {
  throw new Error(
    "Administrator email is required."
  );
}

const allowedInternalRoles =
  new Set([
    "owner_admin",
    "operations_admin",
    "hr_admin",
    "credentialing_admin",
    "finance_admin",
  ]);

if (
  !allowedInternalRoles.has(role)
) {
  throw new Error(
    "Select a valid internal administrator role."
  );
}

if (
  ![
    "all_facilities",
    "selected_facilities",
  ].includes(accessScope)
) {
  throw new Error(
    "Select a valid access scope."
  );
}

if (
  accessScope ===
    "selected_facilities" &&
  allowedFacilityIds.length === 0
) {
  throw new Error(
    "Select at least one facility for this administrator."
  );
}

const tenantId =
  normalizeId(
    selectedTenant
  );

if (
  !tenantId ||
  tenantId === "all"
) {
  throw new Error(
    "Select a specific tenant before inviting an internal administrator."
  );
}


/*
 * =========================================================
 * TENANT / FACILITY INTEGRITY
 * =========================================================
 *
 * The UI already shows tenant-scoped facilities, but
 * provisioning must verify the relationship again before
 * writing authorization data.
 */

if (
  accessScope ===
    "selected_facilities"
) {
  const facilityChecks =
    await Promise.all(
      allowedFacilityIds.map(
        async (facilityId) => {
          const facilitySnap =
            await getDoc(
              doc(
                db,
                "facilityProfiles",
                facilityId
              )
            );

          if (
            !facilitySnap.exists()
          ) {
            throw new Error(
              `Facility profile not found: ${facilityId}`
            );
          }

          const facilityData =
            facilitySnap.data() || {};

          const facilityTenantId =
            normalizeId(
              facilityData?.tenantId ||
                facilityData?.tenantKey ||
                ""
            );

          if (!facilityTenantId) {
            throw new Error(
              `Facility ${facilityId} is missing tenantId.`
            );
          }

          if (
            facilityTenantId !==
            tenantId
          ) {
            throw new Error(
              `Facility ${facilityId} does not belong to tenant ${tenantId}.`
            );
          }

          return facilityId;
        }
      )
    );

  if (
    facilityChecks.length !==
    allowedFacilityIds.length
  ) {
    throw new Error(
      "Unable to validate the selected facility scope."
    );
  }
}


/*
 * For selected-facility administrators, the first
 * authorized facility becomes the deterministic primary
 * facility for compatibility with older modules.
 *
 * All-facility administrators remain tenant-scoped and do
 * not carry a fake facility ID.
 */
const primaryFacilityId =
  accessScope ===
    "selected_facilities"
    ? allowedFacilityIds[0] || ""
    : "";

const canViewAllFacilities =
  accessScope ===
  "all_facilities";

    await setDoc(
      doc(db, "invites", emailLower),
      {
        email: emailLower,
        emailLower,

        name,
        phone,

        accountType: "internal_admin",
        role,
        platformRole: role,
        primaryRole: role.toUpperCase(),
        position: role.toUpperCase(),

        workerType: null,

        tenantId,

facilityId:
  primaryFacilityId || null,

primaryFacilityId:
  primaryFacilityId || null,

facilityIds:
  allowedFacilityIds,

allowedFacilityIds:
  allowedFacilityIds,

accessScope,

/*
 * This is an explicit operational scope override.
 *
 * ROLE_PERMISSIONS supplies the role defaults.
 * A selected-facility administrator must override
 * the default enterprise-wide facility permission.
 *
 * inviteApply.js will consume this in the next step.
 */
permissions: {
  [PERMISSIONS.CAN_VIEW_ALL_FACILITIES]:
    canViewAllFacilities,
},

        inviteStatus: "sent",
        invitationStatus: "sent",
        status: "sent",
        registrationStatus: "invited",

        onboardingStatus: "not_applicable",
        complianceStatus: "not_applicable",
        workerStatus: "not_applicable",

        archived: false,
        active: true,
        disabled: false,

        profileCompleted: true,
        documentsSubmitted: true,
        documentsApproved: true,
        readyForShifts: false,

        source: "internal_admin_management",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    setInternalAdminInviteForm({
  name: "",
  email: "",
  phone: "",
  role: "operations_admin",
  accessScope: "all_facilities",
  allowedFacilityIds: [],
});

    setInternalAdminInviteMessage(
      "Internal administrator invitation created."
    );

    setShowInternalAdminInvite(false);
  } catch (err) {
    console.error(
      "Internal administrator invitation failed:",
      err
    );

    setInternalAdminInviteMessage(
      err?.message ||
        "Internal administrator invitation failed."
    );
  } finally {
    setSavingInternalAdminInvite(false);
  }
};

return (
  <EnterpriseShell
  title="ShiftBridge Enterprise"
subtitle="Real-Time Workforce Visibility"

    activeKey={activeTab}
    navigationItems={ENTERPRISE_NAV_ITEMS.filter(item => item.key !== "sales" || salesSession).map((item) => {
  const registeredCount =
    users?.filter(
      (u) =>
        String(u.registrationStatus || "").toLowerCase() === "pending" ||
        String(u.workerStatus || "").toLowerCase() === "registration_pending"
    ).length || 0;

  const reviewQueueCount =
    users?.filter(
      (u) =>
        String(u.onboardingStatus || "").toLowerCase() === "submitted" ||
        String(u.workerStatus || "").toLowerCase() === "under_review" ||
        String(u.complianceStatus || "").toLowerCase() === "pending_review"
    ).length || 0;

  const readyCount =
    users?.filter(
      (u) =>
        String(u.role || "").toLowerCase() === "worker" &&
        (String(u.workerStatus || "").toLowerCase() === "ready" ||
          String(u.status || "").toLowerCase() === "active")
    ).length || 0;

  const openShiftCount =
    shifts?.filter((s) => String(s.status || "").toLowerCase() === "open")
      .length || 0;

if (item.key === "workers") {
  return { ...item, badge: registeredCount + reviewQueueCount + readyCount };
}

if (item.key === "operations") {
  return { ...item, badge: openShiftCount };
}

  return item;
})}
    onNavigate={setActiveTab}
  >
    <div style={{ maxWidth: 1500, margin: "0 auto" }}>
        
        <div
          style={{
            ...cardStyle(),
            padding: layoutMid ? 18 : 14,
            marginBottom: 12,
            background: "linear-gradient(135deg, rgba(37,99,235,.06), rgba(255,255,255,1))",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: layoutMid ? "center" : "flex-start",
              gap: 12,
              flexDirection: layoutMid ? "row" : "column",
            }}
          >
            <div>
              <div
                style={{
                  color: "#2563eb",
                  fontWeight: 900,
                  fontSize: 12,
                  letterSpacing: ".08em",
                  textTransform: "uppercase",
                }}
              >
                ShiftBridge Enterprise
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: layoutMid ? 22 : 20,
                  fontWeight: 900,
                  color: "#0f172a",
                  lineHeight: 1.05,
                }}
              >
                Real-Time Workforce Visibility
              </div>
              <div
                style={{
                  marginTop: 8,
                  color: "#64748b",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                Manage workforce, facilities, and finance.
              </div>
            </div>

<div
  style={{
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    alignItems: "center",
  }}
>
{isTenantInternalAdmin ? (
  <div
    style={{
      height: 40,
      display: "flex",
      alignItems: "center",
      padding: "0 14px",
      borderRadius: 10,
      border: "1px solid #bfdbfe",
      background: "#eff6ff",
      color: "#1e3a8a",
      fontWeight: 900,
    }}
  >
    {(tenantOptions || []).find(
      (tenant) =>
        normalizeId(tenant?.tenantId) ===
        profileTenantId
    )?.tenantName ||
      profile?.tenantName ||
      profileTenantId}
  </div>
) : (
  <select
    value={selectedTenant}
    onChange={(e) => {
      setSelectedTenant(e.target.value);
      setSelectedFacility("all");
    }}
    style={{
      height: 40,
      borderRadius: 10,
      border: "1px solid #d8e1ea",
      padding: "0 12px",
      background: "#fff",
      fontWeight: 800,
      color: "#0f172a",
    }}
  >
    <option value="all">All tenants</option>

    {(tenantOptions || []).map((tenant) => (
      <option
        key={tenant.tenantId}
        value={tenant.tenantId}
      >
        {tenant.tenantName}
      </option>
    ))}
  </select>
)}

{!isTenantInternalAdmin ? (
  <button
    type="button"
    onClick={openNewTenantEditor}
    style={{
      height: 40,
      padding: "0 14px",
      borderRadius: 10,
      background: "#0f766e",
      color: "#ffffff",
      border: "none",
      fontWeight: 900,
      cursor: "pointer",
    }}
  >
    + Create Tenant
  </button>
) : null}

{!isTenantInternalAdmin && editingTenant ? createPortal(
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(15,23,42,.45)",
      zIndex: 9999,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: 20,
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: 920,
        maxHeight: "90vh",
        overflowY: "auto",
        background: "#ffffff",
        borderRadius: 24,
        padding: 20,
        boxShadow: "0 25px 60px rgba(2,6,23,.25)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 18,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 950,
              color: "#0f172a",
            }}
          >
            {editingTenant?.isNew
              ? "Create New Tenant"
              : "Edit Tenant"}
          </div>

          <div
            style={{
              marginTop: 4,
              color: "#64748b",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {editingTenant?.isNew
              ? "Create the tenant workspace without manually entering Firestore data."
              : editingTenant?.tenantName ||
                editingTenant?.tenantId}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleSaveTenantProfile();
            }}
            style={{
              ...buttonBaseStyle(),
              minHeight: 36,
              background: "#2563eb",
              color: "#ffffff",
              border: "1px solid #2563eb",
            }}
          >
            {editingTenant?.isNew
              ? "Create Tenant"
              : "Save Changes"}
          </button>

          <button
            type="button"
            onClick={() => setEditingTenant(null)}
            style={{
              ...buttonBaseStyle(),
              minHeight: 36,
            }}
          >
            Close
          </button>
        </div>
      </div>

      {[
        {
          title: "Tenant Organization",
          description:
            "Core identity and operating model for this tenant.",
          fields: [
            {
              key: "tenantName",
              label: "Legal Tenant Name",
              placeholder: "NewCare Staffing LLC",
              required: true,
            },
            {
              key: "tenantId",
              label: "Tenant ID",
              placeholder: "Generated from tenant name",
              disabled: true,
            },
            {
              key: "displayName",
              label: "Display Name",
              placeholder: "NewCare Staffing",
            },
            {
              key: "tenantType",
              label: "Tenant Type",
              options: [
                {
                  value: "staffing_agency",
                  label: "Staffing Agency",
                },
                {
                  value: "hospital_enterprise",
                  label: "Hospital Enterprise",
                },
                {
                  value: "hybrid_network",
                  label: "Hybrid Network",
                },
              ],
            },
            {
              key: "billingModel",
              label: "Billing Model",
              options: [
                {
                  value: "agency_billing",
                  label: "Agency Billing",
                },
                {
                  value: "contract_billrate",
                  label: "Contract Bill Rate",
                },
                {
                  value: "internal_cost_center",
                  label: "Internal Cost Center",
                },
                {
                  value: "blended",
                  label: "Blended",
                },
              ],
            },
            {
              key: "payrollModel",
              label: "Payroll Model",
              options: [
                {
                  value: "agency_managed",
                  label: "Agency Managed",
                },
                {
                  value: "hospital_managed",
                  label: "Hospital Managed",
                },
                {
                  value: "hybrid",
                  label: "Hybrid",
                },
              ],
            },
          ],
        },
        {
          title: "Tenant Status",
          description:
            "New tenants remain in onboarding until ShiftBridge approves go-live.",
          fields: [
            {
              key: "status",
              label: "Tenant Status",
              options: [
                {
                  value: "onboarding",
                  label: "Onboarding",
                },
                {
                  value: "active",
                  label: "Active",
                },
                {
                  value: "suspended",
                  label: "Suspended",
                },
                {
                  value: "inactive",
                  label: "Inactive",
                },
              ],
            },
            {
              key: "subscriptionStatus",
              label: "Subscription Status",
              options: [
                {
                  value: "pending_activation",
                  label: "Pending Activation",
                },
                {
                  value: "active",
                  label: "Active",
                },
                {
                  value: "past_due",
                  label: "Past Due",
                },
                {
                  value: "suspended",
                  label: "Suspended",
                },
                {
                  value: "cancelled",
                  label: "Cancelled",
                },
              ],
            },
            {
              key: "onboardingStatus",
              label: "Onboarding Status",
              options: [
                {
                  value: "tenant_created",
                  label: "Tenant Created",
                },
                {
                  value: "configuration",
                  label: "Configuration",
                },
                {
                  value: "training",
                  label: "Training",
                },
                {
                  value: "pilot",
                  label: "Pilot",
                },
                {
                  value: "completed",
                  label: "Completed",
                },
              ],
            },
          ],
        },
        {
          title: "Primary Contact",
          description:
            "Main implementation and business contact for the tenant.",
          fields: [
            {
              key: "primaryContactName",
              label: "Primary Contact Name",
              placeholder: "Jane Smith",
            },
            {
              key: "primaryContactEmail",
              label: "Primary Contact Email",
              placeholder: "jane@newcarestaffing.com",
              type: "email",
            },
            {
              key: "primaryContactPhone",
              label: "Primary Contact Phone",
              placeholder: "(000) 000-0000",
              type: "tel",
            },
          ],
        },
        {
          title: "Billing Contact",
          description:
            "Contact responsible for subscription and account billing.",
          fields: [
            {
              key: "billingContactName",
              label: "Billing Contact Name",
              placeholder: "Billing contact",
            },
            {
              key: "billingContactEmail",
              label: "Billing Contact Email",
              placeholder: "billing@newcarestaffing.com",
              type: "email",
            },
          ],
        },
        {
          title: "Business Address",
          description:
            "Legal or primary operating address for the tenant.",
          fields: [
            {
              key: "address",
              label: "Street Address",
              placeholder: "Street address",
              fullWidth: true,
            },
            {
              key: "city",
              label: "City",
              placeholder: "Dallas",
            },
            {
              key: "state",
              label: "State",
              placeholder: "TX",
            },
            {
              key: "zip",
              label: "ZIP Code",
              placeholder: "75000",
            },
            {
              key: "country",
              label: "Country",
              placeholder: "USA",
            },
            {
              key: "phone",
              label: "Company Phone",
              placeholder: "(000) 000-0000",
              type: "tel",
            },
            {
              key: "email",
              label: "Company Email",
              placeholder: "info@newcarestaffing.com",
              type: "email",
            },
            {
              key: "website",
              label: "Website",
              placeholder: "https://newcarestaffing.com",
              type: "url",
              fullWidth: true,
            },
          ],
        },
        {
          title: "Tenant Branding",
          description:
            "Branding inherited by the tenant’s facilities and workspace.",
          fields: [
            {
              key: "primaryColor",
              label: "Primary Color",
              type: "color",
            },
            {
              key: "logoUrl",
              label: "Logo URL",
              placeholder: "https://...",
              type: "url",
              fullWidth: true,
            },
          ],
        },
      ].map((section) => (
        <div
          key={section.title}
          style={{
            marginTop: 14,
            padding: 16,
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            background: "#f8fafc",
          }}
        >
          <div
            style={{
              color: "#0f172a",
              fontSize: 15,
              fontWeight: 950,
            }}
          >
            {section.title}
          </div>

          <div
            style={{
              marginTop: 3,
              color: "#64748b",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {section.description}
          </div>

          <div
            style={{
              marginTop: 12,
              display: "grid",
              gridTemplateColumns: layoutMid
                ? "repeat(2, minmax(0, 1fr))"
                : "1fr",
              gap: 12,
            }}
          >
            {section.fields.map((field) => {
              const isGeneratedTenantId =
                field.key === "tenantId" &&
                editingTenant?.isNew;

              const fieldValue = isGeneratedTenantId
                ? generateUniqueTenantId(
                    editingTenant?.tenantName
                  )
                : editingTenant?.[field.key] || "";

              return (
                <div
                  key={field.key}
                  style={{
                    gridColumn:
                      field.fullWidth && layoutMid
                        ? "1 / -1"
                        : "auto",
                  }}
                >
                  <div style={fieldLabelStyle()}>
                    {field.label}
                    {field.required ? " *" : ""}
                  </div>

                  {field.options ? (
                    <select
                      value={fieldValue}
                      onChange={(event) =>
                        setEditingTenant((current) => ({
                          ...current,
                          [field.key]: event.target.value,
                        }))
                      }
                      style={{
                        ...inputStyle(),
                        background: "#ffffff",
                      }}
                    >
                      {field.options.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type || "text"}
                      value={fieldValue}
                      disabled={
                        field.disabled ||
                        isGeneratedTenantId
                      }
                      placeholder={field.placeholder || ""}
                      onChange={(event) =>
                        setEditingTenant((current) => ({
                          ...current,
                          [field.key]: event.target.value,
                        }))
                      }
                      style={{
                        ...inputStyle(),
                        minHeight:
                          field.type === "color" ? 44 : undefined,
                        padding:
                          field.type === "color"
                            ? 5
                            : inputStyle().padding,
                        background:
                          field.disabled ||
                          isGeneratedTenantId
                            ? "#e2e8f0"
                            : "#ffffff",
                        cursor:
                          field.disabled ||
                          isGeneratedTenantId
                            ? "not-allowed"
                            : "text",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {editingTenant?.isNew ? (
        <div
          style={{
            marginTop: 14,
            padding: 13,
            borderRadius: 12,
            border: "1px solid #bfdbfe",
            background: "#eff6ff",
            color: "#1e40af",
            fontSize: 12,
            fontWeight: 800,
          }}
        >
          Creating this tenant establishes its workspace in
          onboarding status. Facilities and the first Owner Admin
          will be configured next.
        </div>
      ) : null}
    </div>
  </div>,
  document.body
) : null}

{!isTenantInternalAdmin ? (
  <button
    onClick={() => openTenantEditor(selectedTenant)}
    disabled={selectedTenant === "all"}
    style={{
      height: 40,
      padding: "0 14px",
      borderRadius: 10,
      background:
        selectedTenant === "all"
          ? "#e2e8f0"
          : "#2563eb",
      color:
        selectedTenant === "all"
          ? "#64748b"
          : "#fff",
      border: "none",
      fontWeight: 800,
      cursor:
        selectedTenant === "all"
          ? "not-allowed"
          : "pointer",
    }}
  >
    Edit Tenant
  </button>
) : null}

<select
  value={selectedFacility}
  disabled={(scopedFacilities || []).length === 0}
  onChange={(e) => {
    const next = e.target.value;

    if (next) {
      setSelectedFacility(next);
    }
  }}
  style={{
    height: 40,
    borderRadius: 10,
    border: "1px solid #d8e1ea",
    padding: "0 12px",
    background:
      (scopedFacilities || []).length === 0
        ? "#f1f5f9"
        : "#fff",
    fontWeight: 800,
    color:
      (scopedFacilities || []).length === 0
        ? "#64748b"
        : "#0f172a",
    cursor:
      (scopedFacilities || []).length === 0
        ? "not-allowed"
        : "pointer",
  }}
>
  <option value="all">
    {(scopedFacilities || []).length > 0
      ? "All facilities"
      : "No facilities configured"}
  </option>

  {(scopedFacilities || []).map((facility) => (
    <option
      key={facility.id || facility.facilityId}
      value={normalizeId(
        facility.id || facility.facilityId
      )}
    >
      {facility.name ||
        facility.facilityName ||
        facility.id}
    </option>
  ))}
</select>
  
              <button onClick={handleSignOut} style={buttonBaseStyle()}>
                Sign out
              </button>
            </div>
          </div>

          <div
            style={{
              marginTop: 14,
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
          

          </div>
        </div>

{error ? (
  <div
    style={{
      ...cardStyle(),
      padding: 12,
      marginBottom: 12,
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#991b1b",
      fontWeight: 800,
    }}
  >
    {error}
  </div>
) : null}

{loading ? (
  <LoadingState title="Loading enterprise dashboard…" />
) : null}

{!loading && activeTab === "overview" ? (
  <HomeDashboard tenants={tenantOptions}
  shifts={shiftsFiltered}
  facilities={scopedFacilities.map(facility => ({...facility, timeZone: resolveShiftTimeZone(null, facility, tenantOptions.find(tenant => (tenant.id || tenant.tenantId) === facility.tenantId))}))}
    viewportWidth={viewportWidth}
    layoutWide={layoutWide}
    metrics={metrics}
    adminLogs={adminLogs}
    facilityStats={facilityStats}
    recentShifts={recentShifts}
    facilitiesById={facilitiesById}
    usersByUid={usersByUid}
    cardStyle={cardStyle}
    sectionTitle={sectionTitle}
    MetricCard={MetricCard}
    fmtHours={fmtHours}
    fmtMoney={fmtMoney}
    fmtLogTime={fmtLogTime}
    fmtDateTime={fmtDateTime}
    niceAction={niceAction}
    niceRole={niceRole}
        onOpenFacility={(facilityId) => {
      const normalizedFacilityId = normalizeId(facilityId);

      if (!normalizedFacilityId) {
        return;
      }

      const facility = (scopedFacilities || []).find(
        (record) =>
          normalizeId(record?.id || record?.facilityId) ===
          normalizedFacilityId
      );

      if (!facility) {
        return;
      }

      setSelectedFacility(normalizedFacilityId);
      setManagingFacilityAccounts(null);
      setCreatingFacility(false);
      setFacility360Section("overview");
      setEditingFacility(facility);
      setFacilitiesTab("manage");
      setActiveTab("facilities");
    }}
    getAssignedUid={getAssignedUid}
    statusTone={statusTone}
    roleColorFor={roleColorFor}
  />
) : null}
        
        {!loading && activeTab === "approvals" ? (
<AdminComplianceCenter
  tenantId={selectedTenant && selectedTenant !== "all" ? selectedTenant : profile?.tenantId}
  users={users}
  approvalOnly
  selectedWorker={selectedOnboardingWorker}
  onNavigate={(tab) => {
    setActiveTab(tab);

    if (tab === "workers") {
      setWorkersTab("workforce");
    }
  }}
/>
) : null}

{!loading && activeTab === "operations" ? (
  <OperationsCenter
    profile={profile}
    selectedTenant={
      selectedTenant !== "all"
        ? selectedTenant
        : normalizeId(
            facilitiesById.get(
              normalizeId(
                selectedFacility
              )
            )?.tenantId ||
              profile?.tenantId ||
              ""
          )
    }
    selectedFacility={
      selectedFacility
    }
    cardStyle={cardStyle}
  />
) : null}


{!loading && activeTab === "finance" && !financialViewReady ? (
  <section role="status" style={{ ...cardStyle(), padding: 20 }}>
    <h2>Financial information unavailable</h2>
    <p>Financial access and data readiness have not been confirmed for this tenant. Totals and financial actions are unavailable until setup is verified.</p>
  </section>
) : null}

{!loading && activeTab === "finance" && financialViewReady ? (
  <div style={{ display: "grid", gap: 12 }}>
    <div style={{ ...cardStyle(), padding: 14 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[
          ["invoices", "Invoices"],
          ["funding", "Funding"],
          ["payroll", "Payroll"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFinanceTab(key)}
            style={{
              ...buttonBaseStyle(),
              background: financeTab === key ? "#2563eb" : "#fff",
              color: financeTab === key ? "#fff" : "#0f172a",
              border:
                financeTab === key
                  ? "1px solid #2563eb"
                  : "1px solid #d8e1ea",
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>

    {financeTab === "invoices" ? (
      <InvoiceCenter
        viewportWidth={viewportWidth}
        metrics={metrics}
        cardStyle={cardStyle}
        sectionTitle={sectionTitle}
        invoiceDateFrom={invoiceDateFrom}
        invoiceDateTo={invoiceDateTo}
        setInvoiceDateFrom={setInvoiceDateFrom}
        setInvoiceDateTo={setInvoiceDateTo}
        billingRates={billingRates}
        setBillingRates={setBillingRates}
        canViewRates={canViewRates}
        canEditRates={canEditRates}
        showBillingRates={showBillingRates}
        invoiceDrafts={invoicePreview}
        invoices={invoices}
        sending={sending}
        generateSingleInvoice={generateSingleInvoice}
        exportInvoicePdf={exportInvoicePdf}
        emailInvoice={emailInvoice}
        submitInvoiceForFunding={submitInvoiceForFunding}
        selectedFundingCompany={selectedFundingCompany}
        setSelectedFundingCompany={setSelectedFundingCompany}
        factoringCompanies={FACTORING_COMPANIES}
        handleMarkInvoiceSent={handleMarkInvoiceSent}
        handleMarkInvoicePaid={handleMarkInvoicePaid}
        handleVoidInvoice={handleVoidInvoice}
        canGenerateInvoices={canGenerateInvoices}
        canMarkInvoicesPaid={canMarkInvoicesPaid}
        canVoidInvoices={canVoidInvoices}
        fmtMoney={fmtMoney}
        fmtHours={fmtHours}
        fmtDate={fmtDate}
        buttonBaseStyle={buttonBaseStyle}
        primaryButtonStyle={primaryButtonStyle}
        MetricCard={MetricCard}
      />
    ) : null}

    {financeTab === "funding" ? (
      <FundingCenter
        cardStyle={cardStyle}
        invoices={invoices}
        factoringCompanies={FACTORING_COMPANIES}
        fmtMoney={fmtMoney}
        fmtDate={fmtDate}
        markFundingFunded={markFundingFunded}
        markFundingCollected={markFundingCollected}
        buttonBaseStyle={buttonBaseStyle}
      />
    ) : null}

    {financeTab === "payroll" ? (
  <PayrollCenter
    key={selectedTenant || profileTenantId}
    tenantId={selectedTenant && selectedTenant !== "all" ? selectedTenant : profileTenantId}
    loading={loading}
    viewportWidth={viewportWidth}
    shifts={shifts}
    payrollShifts={payrollShifts}
    facilities={facilities}
    facilitiesById={facilitiesById}
    usersByUid={usersByUid}
    cardStyle={cardStyle}
    sectionTitle={sectionTitle}
    buttonBaseStyle={buttonBaseStyle}
    fmtMoney={fmtMoney}
    fmtHours={fmtHours}
    fmtDate={fmtDate}
    niceRole={niceRole}
    roleKey={roleKey}
    toMillis={toMillis}
    getAssignedUid={getAssignedUid}
    isActiveCompletedShift={isActiveCompletedShift}
    setSelectedPayrollShift={setSelectedPayrollShift}
    setShowPayrollStub={setShowPayrollStub}
  />
    ) : null}
  </div>
) : null}

{showPayrollStub && selectedPayrollShift ? (
  <PayrollStubDrawer
    shift={selectedPayrollShift}
    facilities={facilities}
    facilitiesById={facilitiesById}
    usersByUid={usersByUid}
    onClose={() => setShowPayrollStub(false)}
    cardStyle={cardStyle}
    buttonBaseStyle={buttonBaseStyle}
    fmtMoney={fmtMoney}
    fmtHours={fmtHours}
    fmtDate={fmtDate}
    niceRole={niceRole}
    roleKey={roleKey}
    toMillis={toMillis}
    getAssignedUid={getAssignedUid}
  />
) : null}


{!loading && activeTab === "sales" ? (salesSession ?
  <SalesWorkspace key={`${salesSession.uid}:${salesSession.tenantId}`} session={salesSession} /> :
  <p role="alert">Sales requires active canonical Owner Admin access.</p>) : null}

{!loading && activeTab === "compliance" ? (
<AdminComplianceCenter
  tenantId={selectedTenant && selectedTenant !== "all" ? selectedTenant : profile?.tenantId}
  users={(users || []).filter((u) => {
    const onboardingStatus = String(u.onboardingStatus || "").toLowerCase();
    const complianceStatus = String(u.complianceStatus || "").toLowerCase();
    const workerStatus = String(u.workerStatus || "").toLowerCase();
    const status = String(u.status || "").toLowerCase();

    return (
      onboardingStatus === "submitted" ||
      onboardingStatus === "missing_docs" ||
      onboardingStatus === "action_required" ||
      onboardingStatus === "reupload_required" ||
      workerStatus === "action_required" ||
      status === "action_required" ||
      complianceStatus === "pending_review" ||
      complianceStatus === "not_ready" ||
      complianceStatus === "reupload_required" ||
      workerStatus === "under_review" ||
      status === "under_review"
    );
  })}
  facilities={facilities}
  onNavigate={(tab) => {
  setActiveTab(tab);

  if (tab === "workers") {
    setWorkersTab("workforce");
  }
}}
/>
) : null}

        {!loading && activeTab === "alerts" ? (
          <div style={{ ...cardStyle(), padding: 14 }}>
            {sectionTitle("Operational alerts", "Urgent staffing and arrival risk")}
            <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
              {alerts.length === 0 ? (
                <div style={{ color: "#64748b", fontWeight: 800 }}>No active alerts.</div>
              ) : (
                alerts.map((s) => {
  const operational =
    getShiftOperationalSummary(
      s
    );

  const tone =
    statusTone(
      operational.status,
      operational
    );

  const workerUid =
    operational
      .assignedWorkerUid ||
    getAssignedUid(s);

  const adminStatusLabel =
    getAdminShiftStatusLabel(
      s
    );
                  const workerLabel = usersByUid.get(workerUid)?.email || workerUid || "Unassigned";
                  const facilityName =
                    facilitiesById.get(String(s.facilityId || "").toLowerCase())?.name ||
                    s.facilityId ||
                    "—";

                  return (
                    <div
                      key={s.id}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 12,
                        padding: 12,
                        background: "#fff",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: viewportWidth < 900 ? "1fr" : "1.3fr 1fr auto",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontWeight: 900,
                              color: roleColorFor(s.role).color,
                              fontSize: 14,
                            }}
                          >
                            {niceRole(s.role)} • {facilityName}
                          </div>
                          <div
                            style={{
                              color: "#334155",
                              fontSize: 12,
                              fontWeight: 800,
                              marginTop: 2,
                            }}
                          >
                            {fmtDateTime(s.startTime)}
                          </div>
                        </div>

                        <div
                          style={{
                            color: "#64748b",
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        >
                          Worker: {workerLabel}
                        </div>

                        <span
                          style={{
                            background: tone.bg,
                            border: `1px solid ${tone.bd}`,
                            color: tone.fg,
                            borderRadius: 999,
                            padding: "4px 9px",
                            fontSize: 11,
                            fontWeight: 900,
                            justifySelf: viewportWidth < 900 ? "start" : "end",
                          }}
                        >
                          {adminStatusLabel}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : null}
        

        {!loading && activeTab === "users_access" ? (
          <div style={{ display: "grid", gap: 12 }}>
            <div style={{ ...cardStyle(), padding: 18 }}>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 950,
                  color: "#0f172a",
                }}
              >
                Users & Access
              </div>

              <div
                style={{
                  marginTop: 5,
                  color: "#64748b",
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                Manage tenant internal administrators, roles, and facility access.
              </div>
            </div>

            <AdminMigrationTools profile={profile} />

            <div style={{ ...cardStyle(), padding: 18 }}>
             <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
  }}
>
  <div>
    <div
      style={{
        fontSize: 17,
        fontWeight: 950,
        color: "#0f172a",
      }}
    >
      Internal Admins
    </div>

    <div
      style={{
        marginTop: 4,
        color: "#64748b",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      These accounts belong to the selected tenant’s internal personnel—not facility users.
    </div>
  </div>

  <button
    type="button"
   onClick={() => {
  setInternalAdminInviteMessage("");
  setShowInternalAdminInvite((current) => !current);
}}
    style={{
      border: "none",
      borderRadius: 10,
      padding: "10px 14px",
      background: showInternalAdminInvite
        ? "#e2e8f0"
        : "#2563eb",
      color: showInternalAdminInvite
        ? "#334155"
        : "#ffffff",
      fontSize: 12,
      fontWeight: 900,
      cursor: "pointer",
    }}
  >
    {showInternalAdminInvite
      ? "Cancel Invitation"
      : "Invite Internal Admin"}
  </button>
</div>

{showInternalAdminInvite ? (
  <div
    style={{
      marginTop: 16,
      padding: 16,
      borderRadius: 14,
      border: "1px solid #bfdbfe",
      background: "#f8fbff",
    }}
  >
    <div
      style={{
        fontSize: 15,
        fontWeight: 950,
        color: "#0f172a",
      }}
    >
      New Internal Administrator
    </div>

    <div
      style={{
        marginTop: 12,
        display: "grid",
        gridTemplateColumns:
          layoutMid ? "repeat(2, minmax(0, 1fr))" : "1fr",
        gap: 10,
      }}
    >
      <input
        className="input"
        type="text"
        value={internalAdminInviteForm.name}
        onChange={(event) =>
          setInternalAdminInviteForm((current) => ({
            ...current,
            name: event.target.value,
          }))
        }
        placeholder="Full name"
      />

      <input
        className="input"
        type="email"
        value={internalAdminInviteForm.email}
        onChange={(event) =>
          setInternalAdminInviteForm((current) => ({
            ...current,
            email: event.target.value,
          }))
        }
        placeholder="Email address"
      />

      <input
        className="input"
        type="tel"
        value={internalAdminInviteForm.phone}
        onChange={(event) =>
          setInternalAdminInviteForm((current) => ({
            ...current,
            phone: event.target.value,
          }))
        }
        placeholder="Phone number (optional)"
      />

      <select
        className="input"
        value={internalAdminInviteForm.role}
        onChange={(event) =>
          setInternalAdminInviteForm((current) => ({
            ...current,
            role: event.target.value,
          }))
        }
      >
        <option value="operations_admin">
          Operations Admin
        </option>
        <option value="hr_admin">HR Admin</option>
        <option value="credentialing_admin">
          Credentialing Admin
        </option>
        <option value="finance_admin">Finance Admin</option>
        <option value="owner_admin">Owner Admin</option>
      </select>
<select
  className="input"
  value={internalAdminInviteForm.accessScope}
  onChange={(event) =>
    setInternalAdminInviteForm((current) => ({
      ...current,
      accessScope: event.target.value,
      allowedFacilityIds:
        event.target.value === "all_facilities"
          ? []
          : current.allowedFacilityIds,
    }))
  }
>
  <option value="all_facilities">
    All Facilities in Selected Tenant
  </option>
  <option value="selected_facilities">
    Selected Facilities Only
  </option>
</select>

{internalAdminInviteForm.accessScope ===
"selected_facilities" ? (
  <div
    style={{
      gridColumn: layoutMid ? "1 / -1" : "auto",
      padding: 14,
      borderRadius: 12,
      border: "1px solid #dbe4ee",
      background: "#ffffff",
    }}
  >
    <div
      style={{
        color: "#0f172a",
        fontSize: 13,
        fontWeight: 950,
      }}
    >
      Select Accessible Facilities
    </div>

    <div
      style={{
        marginTop: 4,
        color: "#64748b",
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      Only facilities belonging to the currently selected tenant are shown.
    </div>

    {(scopedFacilities || []).length === 0 ? (
      <div
        style={{
          marginTop: 10,
          padding: 12,
          borderRadius: 10,
          border: "1px dashed #cbd5e1",
          background: "#f8fafc",
          color: "#64748b",
          fontSize: 12,
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        No facilities are available for the selected tenant.
      </div>
    ) : (
      <div
        style={{
          marginTop: 10,
          display: "grid",
          gridTemplateColumns: layoutMid
            ? "repeat(2, minmax(0, 1fr))"
            : "1fr",
          gap: 8,
        }}
      >
        {(scopedFacilities || []).map((facility) => {
          const facilityId = normalizeId(
            facility?.id || facility?.facilityId
          );

          const isChecked =
            internalAdminInviteForm.allowedFacilityIds.includes(
              facilityId
            );

          return (
            <label
              key={facilityId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                padding: 10,
                borderRadius: 10,
                border: isChecked
                  ? "1px solid #93c5fd"
                  : "1px solid #e2e8f0",
                background: isChecked
                  ? "#eff6ff"
                  : "#ffffff",
                color: "#0f172a",
                fontSize: 12,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() =>
                  setInternalAdminInviteForm((current) => {
                    const currentIds =
                      current.allowedFacilityIds || [];

                    return {
                      ...current,
                      allowedFacilityIds: currentIds.includes(
                        facilityId
                      )
                        ? currentIds.filter(
                            (id) => id !== facilityId
                          )
                        : [...currentIds, facilityId],
                    };
                  })
                }
              />

              <span>
                {facility?.name ||
                  facility?.facilityName ||
                  facilityId}
              </span>
            </label>
          );
        })}
      </div>
    )}
  </div>
) : null}

    </div>

    {internalAdminInviteMessage ? (
  <div
    style={{
      marginTop: 12,
      padding: 11,
      borderRadius: 10,
      border: "1px solid #cbd5e1",
      background: "#ffffff",
      color: "#334155",
      fontSize: 12,
      fontWeight: 800,
    }}
  >
    {internalAdminInviteMessage}
  </div>
) : null}

  <button
  type="button"
  onClick={handleCreateInternalAdminInvite}
  disabled={savingInternalAdminInvite}
      style={{
        marginTop: 12,
        width: "100%",
        border: "none",
        borderRadius: 10,
        padding: "11px 14px",
        background: savingInternalAdminInvite
          ? "#94a3b8"
          : "#2563eb",
        color: "#ffffff",
        fontSize: 12,
        fontWeight: 950,
        cursor: savingInternalAdminInvite
          ? "not-allowed"
          : "pointer",
      }}
    >
      {savingInternalAdminInvite
        ? "Creating Invitation..."
        : "Create Internal Admin Invitation"}
    </button>
  </div>
) : null}

              {(() => {
                const internalRoles = new Set([
                  "owner_admin",
                  "operations_admin",
                  "hr_admin",
                  "credentialing_admin",
                  "finance_admin",
                ]);

              const registeredInternalAdmins = (users || [])
                .filter((user) => {
                  const role = String(
                    user?.platformRole || user?.role || ""
                  )
                    .trim()
                    .toLowerCase();

                  const userTenantId = normalizeId(user?.tenantId);
                  const selectedTenantId = normalizeId(selectedTenant);

                  const matchesSelectedTenant =
                    selectedTenantId === "all" ||
                    userTenantId === selectedTenantId;

                  return (
                    internalRoles.has(role) &&
                    !user?.archived &&
                    matchesSelectedTenant
                  );
                })
                .map((user) => ({
                  ...user,
                  recordType: "user",
                  displayStatus:
                    user?.disabled === true
                      ? "disabled"
                      : user?.status || "active",
                }));

              const pendingInternalAdminInvites = (inviteRecords || [])
                .filter((invite) => {
                  const role = String(
                    invite?.platformRole || invite?.role || ""
                  )
                    .trim()
                    .toLowerCase();

                  const inviteTenantId = normalizeId(invite?.tenantId);
                  const selectedTenantId = normalizeId(selectedTenant);

                  const matchesSelectedTenant =
                    selectedTenantId === "all" ||
                    inviteTenantId === selectedTenantId;

                  return (
                    internalRoles.has(role) &&
                    String(invite?.accountType || "").toLowerCase() ===
                    "internal_admin" &&
                    !invite?.archived &&
                    matchesSelectedTenant
                  );
                })
                .map((invite) => ({
                  ...invite,
                  recordType: "invite",
                  displayStatus:
                    invite?.inviteStatus ||
                    invite?.invitationStatus ||
                    invite?.status ||
                    "sent",
                }));

              const internalAdmins = [
                ...registeredInternalAdmins,
                ...pendingInternalAdminInvites,
              ];

                const roleLabels = {
                  owner_admin: "Owner Admin",
                  operations_admin: "Operations Admin",
                  hr_admin: "HR Admin",
                  credentialing_admin: "Credentialing Admin",
                  finance_admin: "Finance Admin",
                };

                if (internalAdmins.length === 0) {
                  return (
                    <div
                      style={{
                        marginTop: 14,
                        padding: 18,
                        borderRadius: 12,
                        border: "1px dashed #cbd5e1",
                        background: "#f8fafc",
                        color: "#64748b",
                        textAlign: "center",
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      No internal administrator accounts were found.
                    </div>
                  );
                }

                return (
                  <div
                    style={{
                      marginTop: 14,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    {internalAdmins.map((admin, index) => {
                      const role = String(
                        admin?.platformRole || admin?.role || ""
                      )
                        .trim()
                        .toLowerCase();

                      const adminStatus = String(
                        admin?.displayStatus ||
                        admin?.inviteStatus ||
                        admin?.invitationStatus ||
                        admin?.status ||
                        ""
                      )
                        .trim()
                        .toLowerCase();

                      const isDisabled =
                        admin?.disabled === true ||
                        adminStatus === "disabled";

                      const isInvitation =
                        admin?.recordType === "invite" &&
                        ["sent", "invited", "pending"].includes(adminStatus);

                      const statusLabel = isDisabled
                        ? "Disabled"
                        : isInvitation
                          ? "Invitation Pending"
                          : "Active";

                      const adminTenantId = String(
                        admin?.tenantId || ""
                      ).trim();

                      const adminAccessScope = String(
                        admin?.accessScope || "all_facilities"
                      )
                        .trim()
                        .toLowerCase();

                      const adminFacilityIds = Array.from(
                        new Set(
                          (
                            admin?.allowedFacilityIds ||
                            admin?.facilityIds ||
                            []
                          )
                            .map(normalizeId)
                            .filter(Boolean)
                        )
                      );

                      const accessScopeLabel =
                        adminAccessScope === "selected_facilities"
                          ? `${adminFacilityIds.length} Selected ${adminFacilityIds.length === 1
                            ? "Facility"
                            : "Facilities"
                          }`
                          : "All Tenant Facilities";

                      const adminKey =
                        admin?.uid ||
                        admin?.id ||
                        admin?.email ||
                        `internal-admin-${index}`;

                      return (
                        <div
                          key={adminKey}
                          style={{
                            padding: 14,
                            border: "1px solid #dbe4ee",
                            borderRadius: 12,
                            background: "#ffffff",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                color: "#0f172a",
                                fontSize: 14,
                                fontWeight: 950,
                              }}
                            >
                              {(() => {
                                const rawName = String(
                                  admin?.name || admin?.displayName || ""
                                ).trim();

                                const technicalRoleNames = [
                                  "owner_admin",
                                  "operations_admin",
                                  "hr_admin",
                                  "credentialing_admin",
                                  "finance_admin",
                                ];

                                return rawName &&
                                  !technicalRoleNames.includes(rawName.toLowerCase())
                                  ? rawName
                                  : roleLabels[role] || "Internal Administrator";
                              })()}
                            </div>

                            <div
                              style={{
                                marginTop: 3,
                                color: "#475569",
                                fontSize: 12,
                                fontWeight: 700,
                                overflowWrap: "anywhere",
                              }}
                            >
                              {admin?.email || "No email recorded"}
                            </div>

                            <div
                              style={{
                                marginTop: 8,
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 6,
                              }}
                            >
                              <span
                                style={{
                                  padding: "5px 8px",
                                  borderRadius: 999,
                                  background: "#eff6ff",
                                  color: "#1d4ed8",
                                  fontSize: 10,
                                  fontWeight: 900,
                                }}
                              >
                                {roleLabels[role] || "Internal Admin"}
                              </span>

                              <span
                                style={{
                                  padding: "5px 8px",
                                  borderRadius: 999,
                                  background: "#f1f5f9",
                                  color: "#475569",
                                  fontSize: 10,
                                  fontWeight: 900,
                                }}
                              >
                                Tenant: {adminTenantId || "Not assigned"}
                              </span>

                              <span
                                style={{
                                  padding: "5px 8px",
                                  borderRadius: 999,
                                  background:
                                    adminAccessScope === "selected_facilities"
                                      ? "#fff7ed"
                                      : "#ecfdf5",
                                  color:
                                    adminAccessScope === "selected_facilities"
                                      ? "#c2410c"
                                      : "#047857",
                                  fontSize: 10,
                                  fontWeight: 900,
                                }}
                              >
                                {accessScopeLabel}
                              </span>

                            </div>
                          </div>

                          <span
                            style={{
                              padding: "5px 9px",
                              borderRadius: 999,
                              background: isDisabled
                                ? "#fef2f2"
                                : isInvitation
                                  ? "#fff7ed"
                                  : "#ecfdf5",
                              color: isDisabled
                                ? "#b91c1c"
                                : isInvitation
                                  ? "#c2410c"
                                  : "#047857",
                              fontSize: 10,
                              fontWeight: 950,
                            }}
                          >
                            {statusLabel}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        ) : null}

{!loading && activeTab === "system" ? (
  <SystemCenter
onArchiveOldShifts={async () => {
  const confirmed =
    window.confirm(
      "Archive only shifts explicitly marked with testData = true and testArchiveEligible = true?"
    );

  if (!confirmed) {
    return;
  }

  try {
    const count =
      await archiveOldTestShifts();

    alert(
      `Archived ${count} explicitly approved test shift${
        count === 1 ? "" : "s"
      }.`
    );
  } catch (err) {
    console.error(
      "Explicit test shift archive failed:",
      err
    );

    alert(
      err?.message ||
        "Unable to archive explicit test shifts."
    );
  }
}}

    onArchiveOldWorkers={async () => {
      if (
        !window.confirm(
          "Archive explicitly marked test workers?"
        )
      ) {
        return;
      }

      const count =
        await archiveOldTestWorkers();

      alert(
        `Archived ${count} explicitly marked test workers.`
      );
    }}

    onSeedTestWorkers={
      handleSeedBridgeWorkerInvites
    }
  />
) : null}

{!loading && activeTab === "activity" ? (
  <div style={{ ...cardStyle(), padding: 14 }}>
    {sectionTitle(
      "Admin Activity",
      "Search and review administrative changes across the platform"
    )}

    <div
      style={{
        marginTop: 12,
        display: "grid",
        gridTemplateColumns:
          viewportWidth < 900
            ? "1fr"
            : "minmax(240px, 1.4fr) minmax(180px, .8fr) minmax(140px, .6fr)",
        gap: 8,
        alignItems: "end",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: 0.35,
            color: "#64748b",
            marginBottom: 5,
          }}
        >
          Search
        </div>

        <input
          value={activitySearch}
          onChange={(e) =>
            setActivitySearch(e.target.value)
          }
          placeholder="worker email, admin email, action..."
          style={{
            width: "100%",
            minHeight: 40,
            padding: "0 12px",
            border: "1px solid #cbd5e1",
            borderRadius: 10,
            background: "#fff",
            fontWeight: 700,
            color: "#0f172a",
          }}
        />
      </div>

      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: 0.35,
            color: "#64748b",
            marginBottom: 5,
          }}
        >
          Action
        </div>

        <select
          value={activityActionFilter}
          onChange={(e) =>
            setActivityActionFilter(
              e.target.value
            )
          }
          style={{
            width: "100%",
            minHeight: 40,
            padding: "0 12px",
            border: "1px solid #cbd5e1",
            borderRadius: 10,
            background: "#fff",
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          <option value="all">
            all actions
          </option>
          <option value="update_worker">
            update_worker
          </option>
        </select>
      </div>

      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: 0.35,
            color: "#64748b",
            marginBottom: 5,
          }}
        >
          Time Range
        </div>

        <select
          value={activityDays}
          onChange={(e) =>
            setActivityDays(e.target.value)
          }
          style={{
            width: "100%",
            minHeight: 40,
            padding: "0 12px",
            border: "1px solid #cbd5e1",
            borderRadius: 10,
            background: "#fff",
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          <option value="7">
            last 7 days
          </option>
          <option value="30">
            last 30 days
          </option>
          <option value="90">
            last 90 days
          </option>
          <option value="0">
            all time
          </option>
        </select>
      </div>
    </div>

    <div
      style={{
        marginTop: 12,
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <span
        style={{
          padding: "4px 8px",
          borderRadius: 999,
          background: "#eff6ff",
          border: "1px solid #bfdbfe",
          color: "#1d4ed8",
          fontSize: 11,
          fontWeight: 900,
        }}
      >
        {filteredAdminLogs.length} visible log
        {filteredAdminLogs.length === 1
          ? ""
          : "s"}
      </span>
    </div>

    <div
      style={{
        marginTop: 12,
        display: "grid",
        gap: 8,
      }}
    >
      {filteredAdminLogs.length === 0 ? (
        <div
          style={{
            color: "#64748b",
            fontWeight: 800,
          }}
        >
          No matching admin activity.
        </div>
      ) : (
        filteredAdminLogs.map((log) => (
          <div
            key={log.id}
            style={{
              border:
                "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 12,
              background: "#fff",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  viewportWidth < 900
                    ? "1fr"
                    : "1fr auto",
                gap: 10,
                alignItems: "start",
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: 900,
                    color: "#0f172a",
                    fontSize: 14,
                  }}
                >
                  {niceAction(log.action)}
                </div>

                <div
                  style={{
                    marginTop: 4,
                    color: "#334155",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  target:{" "}
                  {log.targetEmail ||
                    log.targetUid ||
                    "unknown"}
                </div>

                <div
                  style={{
                    marginTop: 4,
                    color: "#64748b",
                    fontSize: 11,
                    fontWeight: 700,
                    wordBreak: "break-word",
                  }}
                >
                  by{" "}
                  {log.performedBy ||
                    "unknown"}{" "}
                  •{" "}
                  {fmtLogTime(
                    log.createdAt
                  )}
                </div>
              </div>

              <span
                style={{
                  padding: "4px 8px",
                  borderRadius: 999,
                  background: "#f8fafc",
                  border:
                    "1px solid #e2e8f0",
                  color: "#475569",
                  fontSize: 11,
                  fontWeight: 900,
                  whiteSpace: "nowrap",
                  justifySelf:
                    viewportWidth < 900
                      ? "start"
                      : "end",
                }}
              >
                {String(
                  log.action || "action"
                )}
              </span>
            </div>

            {log.changes ? (
              <div
                style={{
                  marginTop: 10,
                  paddingTop: 10,
                  borderTop:
                    "1px solid #f1f5f9",
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                }}
              >
                {Object.entries(
                  log.changes
                ).map(([k, val]) => (
                  <span
                    key={`${log.id}-${k}`}
                    style={{
                      padding: "3px 8px",
                      borderRadius: 999,
                      background:
                        "#f8fafc",
                      border:
                        "1px solid #e2e8f0",
                      color: "#475569",
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    {k}:{" "}
                    {Array.isArray(val)
                      ? val.join(", ")
                      : String(val)}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ))
      )}
    </div>
  </div>
) : null}

{!loading && activeTab === "workers" ? (
  <div style={{ display: "grid", gap: 12 }}>
    <div style={{ ...cardStyle(), padding: 14 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[
          ["pipeline", "Pipeline"],
          ["workforce", "Workforce"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setWorkersTab(key)}
            style={{
              ...buttonBaseStyle(),
              background: workersTab === key ? "#2563eb" : "#fff",
              color: workersTab === key ? "#fff" : "#0f172a",
              border:
                workersTab === key
                  ? "1px solid #2563eb"
                  : "1px solid #d8e1ea",
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>

{workersTab === "pipeline" ? (
  <ApplicantProvider
    profile={profile}
    selectedTenantId={selectedTenant}
    selectedFacilityId={selectedFacility}
  >
<TalentPipeline
  profile={profile}
  facilities={facilities}
      onInviteUser={() => {
        setActiveTab("invitations");
      }}
      onApproveRegistration={approveRegistration}
      onReviewWorker={(worker) => {
        setSelectedOnboardingWorker(worker);
        setActiveTab("approvals");
      }}
      onViewWorker={(worker) => {
        setAttentionTarget(worker.uid || worker.id || worker.email);
        setWorkersTab("workforce");
      }}
      onNavigate={(destination) => {
        if (destination === "workforce") {
          setWorkersTab("workforce");
          return;
        }

        if (destination === "talentPipeline") {
          setActiveTab("workers");
          setWorkersTab("pipeline");
          return;
        }

        setActiveTab(destination);
      }}
    />
  </ApplicantProvider>
) : null}

    {workersTab === "workforce" && canManageUsers ? (
      <div
        id="workforce-attention"
        style={{
          ...cardStyle(),
          padding: 14,
        }}
      >
           <AdminWorkforceManager
  tenantId={selectedTenant || "all"}
  facilityId={adminFacilityId || "all"}
  activeContext={adminActiveContext}
  externalFacilities={facilities}
  externalUsers={[
    ...(scopedUsers || []),
    ...(invitedUsers || []),
  ]}
  shifts={shifts}
  attentionTarget={attentionTarget}
  onApproveInvitedUser={approveInvitedUser}
/>
      </div>
    ) : null}
  </div>
) : null}

{!loading && activeTab === "facilities" ? (
  <div style={{ display: "grid", gap: 12 }}>
    <div style={{ ...cardStyle(), padding: 14 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[
          ["manage", "Manage"],
          ["rates", "Rates"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFacilitiesTab(key)}
            style={{
              ...buttonBaseStyle(),
              background: facilitiesTab === key ? "#2563eb" : "#fff",
              color: facilitiesTab === key ? "#fff" : "#0f172a",
              border:
                facilitiesTab === key
                  ? "1px solid #2563eb"
                  : "1px solid #d8e1ea",
            }}
          >
            {label}
          </button>
        ))}
      </div>
    </div>

    {facilitiesTab === "rates" ? (
      <section style={{ ...cardStyle(), padding: 24 }} aria-label="Facility rates">
        {sectionTitle("Facility rates", "Billing and worker pay rates are managed in each facility profile.")}
        <p style={{ color: "#64748b", lineHeight: 1.6 }}>
          Open a facility, select Edit Facility, then use the Rates Matrix in Profile.
          Existing rate access and editing permissions apply.
        </p>
        <button type="button" style={buttonBaseStyle()} onClick={() => setFacilitiesTab("manage")}>
          View facilities
        </button>
      </section>
    ) : null}

    {facilitiesTab === "manage" ? (
    
    <div style={{ ...cardStyle(), padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        {sectionTitle("Facilities", "Manage tenant facilities, billing structure, visibility, and rates.")}
                <button
onClick={() => {
  const tenantId = normalizeId(selectedTenant);

  if (!tenantId || tenantId === "all") {
    alert("Select a specific tenant first.");
    return;
  }

  const tenantRecord = (tenantOptions || []).find(
    (tenant) =>
      normalizeId(tenant?.tenantId) === tenantId
  );

  setEditingFacility({
    id: "",
    facilityId: "",
    name: "",
    facilityName: "",

    tenantId,
    tenantName:
      tenantRecord?.tenantName || tenantId,
    tenantType:
      tenantRecord?.tenantType ||
      "staffing_agency",

    billingModel:
      tenantRecord?.billingModel ||
      "agency_billing",

    status: "active",
    address: "",
    city: "",
    state: "TX",
    zip: "",

    facilityLat: "",
    facilityLng: "",
    geoRadiusMeters: 200,

    rates: {},
    visibility: {},
  });

  setFacility360Section("profile");
  setCreatingFacility(true);
}}

                  style={{
                    ...buttonBaseStyle(),
                    background: "#2563eb",
                    color: "#fff",
                    border: "1px solid #2563eb",
                  }}
                >
                  Add Facility
                </button>
      </div>

      <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
        {(scopedFacilities || []).map((facility) => (
          <div
            key={facility.id || facility.facilityId}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 16,
              padding: 14,
              background: "#fff",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 900, color: "#0f172a" }}>
                  {facility.name || facility.facilityName || facility.id || facility.facilityId}
                </div>

                <div style={{ marginTop: 4, color: "#64748b", fontSize: 12, fontWeight: 700 }}>
                  Tenant: {facility.tenantName || facility.tenantId || "—"}
                </div>

                <div style={{ marginTop: 4, color: "#64748b", fontSize: 12, fontWeight: 700 }}>
                  Tenant Type: {facility.tenantType || "staffing_agency"}
                </div>

                <div style={{ marginTop: 4, color: "#64748b", fontSize: 12, fontWeight: 700 }}>
                  Billing Model: {facility.billingModel || "agency_billing"}
                </div>

                <div style={{ marginTop: 4, color: "#64748b", fontSize: 12, fontWeight: 700 }}>
                  Status: {facility.status || "active"}
                </div>
              </div>

              <div
  style={{
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  }}
>
  <button
    type="button"
    onClick={() => {
      setEditingFacility(null);
      setCreatingFacility(false);

    const facilityContextId = normalizeId(
  facility?.facilityId || facility?.id
);

if (facilityContextId) {
  setSelectedFacility(facilityContextId);
}

setManagingFacilityAccounts(facility);
setFacilityAccountEmail("");
setFacilityAccountName("");
setFacilityAccountPhone("");
setFacilityAccountRole("facility_planner");
setFacilityAccountMessage("");
    }}
    style={{
      ...buttonBaseStyle(),
      minHeight: 34,
      padding: "6px 12px",
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
    }}
  >
    Manage Accounts
  </button>

  <button
    type="button"
    onClick={() => {
      setManagingFacilityAccounts(null);
      setFacility360Section("overview");
      setCreatingFacility(false);
      setEditingFacility(facility);
    }}
    style={{
      ...buttonBaseStyle(),
      minHeight: 34,
      padding: "6px 12px",
    }}
  >
    Edit Facility
  </button>
</div>
            </div>

            <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(facility.allowedRoles || ["CNA", "LVN", "RN", "MED_AIDE", "CARE_GIVER"]).map((role) => (
                <span
                  key={role}
                  style={{
                    padding: "5px 10px",
                    borderRadius: 999,
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    color: "#1d4ed8",
                    fontSize: 11,
                    fontWeight: 900,
                  }}
                >
                  {role}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
    ) : null}

{managingFacilityAccounts ? (
  <div
    role="presentation"
    onClick={() => {
      setManagingFacilityAccounts(null);
      setFacilityAccountMessage("");
    }}
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 3000,
      background: "rgba(15, 23, 42, 0.45)",
      display: "flex",
      justifyContent: "flex-end",
    }}
  >
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Manage Facility Accounts"
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "min(520px, 100%)",
        height: "100%",
        overflowY: "auto",
        padding: 20,
        background: "#f8fbff",
        borderLeft: "1px solid #bfdbfe",
        boxShadow: "-12px 0 32px rgba(15, 23, 42, 0.18)",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 18,
              fontWeight: 950,
              color: "#0f172a",
            }}
          >
            Manage Facility Accounts
          </div>

          <div
            style={{
              marginTop: 4,
              color: "#64748b",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {managingFacilityAccounts.name ||
              managingFacilityAccounts.facilityName ||
              managingFacilityAccounts.id ||
              managingFacilityAccounts.facilityId}
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setManagingFacilityAccounts(null);
            setFacilityAccountEmail("");
            setFacilityAccountName("");
            setFacilityAccountPhone("");
            setFacilityAccountRole("facility_planner");
            setFacilityAccountMessage("");
          }}
          style={{
            ...buttonBaseStyle(),
            minHeight: 34,
            padding: "6px 12px",
          }}
        >
          Close
        </button>
      </div>

      {facilityAccountMessage ? (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 10,
            background: facilityAccountMessage
              .toLowerCase()
              .includes("created")
              ? "#ecfdf5"
              : "#fef2f2",
            color: facilityAccountMessage
              .toLowerCase()
              .includes("created")
              ? "#047857"
              : "#b91c1c",
            fontWeight: 800,
            fontSize: 13,
          }}
        >
          {facilityAccountMessage}
        </div>
      ) : null}

      <div
        style={{
          marginTop: 14,
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 10,
        }}
      >
        <input
          type="email"
          value={facilityAccountEmail}
          onChange={(e) => setFacilityAccountEmail(e.target.value)}
          placeholder="Account email"
          style={{
            width: "100%",
            minHeight: 42,
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            color: "#0f172a",
            fontSize: 14,
            fontWeight: 700,
            boxSizing: "border-box",
            outline: "none",
          }}
        />

        <input
          type="text"
          value={facilityAccountName}
          onChange={(e) => setFacilityAccountName(e.target.value)}
          placeholder="Account holder name"
          style={{
            width: "100%",
            minHeight: 42,
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            color: "#0f172a",
            fontSize: 14,
            fontWeight: 700,
            boxSizing: "border-box",
            outline: "none",
          }}
        />

        <input
          type="tel"
          value={facilityAccountPhone}
          onChange={(e) => setFacilityAccountPhone(e.target.value)}
          placeholder="Phone — optional"
          style={{
            width: "100%",
            minHeight: 42,
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            color: "#0f172a",
            fontSize: 14,
            fontWeight: 700,
            boxSizing: "border-box",
            outline: "none",
          }}
        />

        <select
          value={facilityAccountRole}
          onChange={(e) => setFacilityAccountRole(e.target.value)}
          style={{
            width: "100%",
            minHeight: 42,
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            background: "#ffffff",
            color: "#0f172a",
            fontSize: 14,
            fontWeight: 700,
            boxSizing: "border-box",
            outline: "none",
          }}
        >
          <option value="facility_planner">
            Facility Planner
          </option>

          <option value="facility_admin_viewer">
            Facility Pulse Viewer
          </option>
        </select>
      </div>

      <div
        style={{
          marginTop: 14,
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <button
          type="button"
          disabled={savingFacilityAccount}
          onClick={async () => {
            try {
              setSavingFacilityAccount(true);
              setFacilityAccountMessage("");

              const emailLower = facilityAccountEmail
                .trim()
                .toLowerCase();

              if (!emailLower) {
                throw new Error("Account email is required.");
              }

              const facilityId =
                managingFacilityAccounts.facilityId ||
                managingFacilityAccounts.id;

              if (!facilityId) {
                throw new Error(
                  "The selected facility does not have a facility ID."
                );
              }

              const tenantId =
                managingFacilityAccounts.tenantId ||
                (selectedTenant !== "all" ? selectedTenant : "");

              await createFacilityInvitation({
                email: emailLower,
                name: facilityAccountName,
                phone: facilityAccountPhone,
                role: facilityAccountRole,
                tenantId,
                facilityId,
              });

              setFacilityAccountEmail("");
              setFacilityAccountName("");
              setFacilityAccountPhone("");
              setFacilityAccountRole("facility_planner");

              setFacilityAccountMessage(
                "Facility account invitation created."
              );
            } catch (err) {
              console.error(
                "Facility account invite failed:",
                err
              );

              setFacilityAccountMessage(
                err?.message ||
                  "Facility account invitation failed."
              );
            } finally {
              setSavingFacilityAccount(false);
            }
          }}
          style={{
            ...buttonBaseStyle(),
            minHeight: 40,
            padding: "8px 14px",
            background: "#2563eb",
            color: "#ffffff",
            border: "1px solid #2563eb",
            opacity: savingFacilityAccount ? 0.65 : 1,
            cursor: savingFacilityAccount
              ? "not-allowed"
              : "pointer",
          }}
        >
           {savingFacilityAccount
        ? "Creating Invitation..."
        : "Create Facility Invitation"}
    </button>
  </div>

  <div
    style={{
      marginTop: 24,
      paddingTop: 18,
      borderTop: "1px solid #dbeafe",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 950,
          color: "#0f172a",
        }}
      >
        Existing Facility Accounts
      </div>

      <div
        style={{
          minWidth: 28,
          height: 28,
          padding: "0 9px",
          borderRadius: 999,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#dbeafe",
          color: "#1d4ed8",
          fontSize: 12,
          fontWeight: 950,
          boxSizing: "border-box",
        }}
      >
        {facilityAccountRecords.length}
      </div>
    </div>

    <div
      style={{
        marginTop: 4,
        color: "#64748b",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      Invitations and registered accounts assigned to this facility.
    </div>

    {facilityAccountRecords.length === 0 ? (
      <div
        style={{
          marginTop: 12,
          padding: 14,
          borderRadius: 12,
          border: "1px dashed #cbd5e1",
          background: "#ffffff",
          color: "#64748b",
          fontSize: 13,
          fontWeight: 700,
          textAlign: "center",
        }}
      >
        No facility accounts have been created yet.
      </div>
    ) : (
      <div
        style={{
          marginTop: 12,
          display: "grid",
          gap: 10,
        }}
      >
        {facilityAccountRecords.map((account, index) => {
          const accountRole = String(
            account?.platformRole ||
              account?.role ||
              account?.accountType ||
              ""
          ).toLowerCase();

          const roleLabel = [
            "facility_planner",
            "facilityplanner",
            "planner",
          ].includes(accountRole)
            ? "Facility Planner"
            : "Facility Pulse Viewer";

          const status = String(
            account?.displayStatus || "pending"
          ).toLowerCase();

          const rawAccountName = String(
  account?.name || account?.displayName || ""
).trim();

const roleCodeNames = [
  "facility_planner",
  "facilityplanner",
  "planner",
  "facility_admin_viewer",
  "facility_pulse",
];

const accountDisplayName =
  rawAccountName &&
  !roleCodeNames.includes(rawAccountName.toLowerCase())
    ? rawAccountName
    : roleLabel;

          const isActive =
            status === "active" ||
            status === "approved" ||
            status === "enabled";

          const isDisabled = status === "disabled";

          const statusLabel = isDisabled
            ? "Disabled"
            : isActive
            ? "Active"
            : status === "sent" ||
              status === "invited" ||
              status === "pending"
            ? "Invitation Pending"
            : status.replaceAll("_", " ");

          const statusColors = isDisabled
            ? {
                background: "#fef2f2",
                color: "#b91c1c",
              }
            : isActive
            ? {
                background: "#ecfdf5",
                color: "#047857",
              }
            : {
                background: "#fff7ed",
                color: "#c2410c",
              };

          const accountKey =
            account?.uid ||
            account?.id ||
            account?.email ||
            `${accountRole}-${index}`;

          return (
            <div
              key={accountKey}
              style={{
                padding: 14,
                borderRadius: 12,
                border: "1px solid #dbe4ee",
                background: "#ffffff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 10,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      color: "#0f172a",
                      fontSize: 14,
                      fontWeight: 950,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {accountDisplayName}
                  </div>

                  <div
                    style={{
                      marginTop: 3,
                      color: "#475569",
                      fontSize: 12,
                      fontWeight: 700,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {account?.email || "No email recorded"}
                  </div>
                </div>

                <div
                  style={{
                    flexShrink: 0,
                    padding: "5px 8px",
                    borderRadius: 999,
                    background: statusColors.background,
                    color: statusColors.color,
                    fontSize: 10,
                    fontWeight: 950,
                    textTransform: "capitalize",
                  }}
                >
                  {statusLabel}
                </div>
              </div>

              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6,
                }}
              >
                <div
                  style={{
                    padding: "5px 8px",
                    borderRadius: 999,
                    background: "#eff6ff",
                    color: "#1d4ed8",
                    fontSize: 10,
                    fontWeight: 900,
                  }}
                >
                  {roleLabel}
                </div>

                <div
                  style={{
                    padding: "5px 8px",
                    borderRadius: 999,
                    background: "#f1f5f9",
                    color: "#475569",
                    fontSize: 10,
                    fontWeight: 900,
                  }}
                >
                  {account?.recordType === "user"
                    ? "Registered Account"
                    : "Invitation"}
                </div>
              </div>

              {account?.phone ? (
                <div
                  style={{
                    marginTop: 8,
                    color: "#64748b",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Phone: {account.phone}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    )}
  </div>
</div>
  </div>
) : null}

<>
{editingFacility ? (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: 20,
      background: "rgba(15,23,42,.45)",
    }}
  >
    <div
      style={{
        width: "100%",
        maxWidth: 1040,
        maxHeight: "90vh",
        overflowY: "auto",
        padding: 20,
        borderRadius: 24,
        background: "#ffffff",
        boxShadow: "0 25px 60px rgba(2,6,23,.25)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 18,
          gap: 12,
        }}
      >
        <div>
          <div
            style={{
              color: "#0f172a",
              fontSize: 20,
              fontWeight: 900,
            }}
          >
            {creatingFacility
              ? "Add Facility"
              : "Facility 360"}
          </div>

          <div
            style={{
              marginTop: 4,
              color: "#64748b",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {editingFacility?.name ||
              editingFacility?.facilityName ||
              editingFacility?.id ||
              "New Facility"}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 10,
          }}
        >
                {[
                  "profile",
                  "contacts",
                  "notes",
                  "workforce",
                  "documents",
                ].includes(facility360Section) ? (
                  <button
              type="button"
              onClick={handleSaveFacilityProfile}
              style={{
                ...buttonBaseStyle(),
                minHeight: 36,
                color: "#ffffff",
                background: "#2563eb",
                border: "1px solid #2563eb",
              }}
            >
              Save
            </button>
          ) : null}

          <button
            type="button"
            onClick={() => {
              setEditingFacility(null);
              setCreatingFacility(false);
              setFacility360Section("overview");
            }}
            style={{
              ...buttonBaseStyle(),
              minHeight: 36,
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Facility 360 tabs */}
      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 16,
          paddingBottom: 10,
          flexWrap: "wrap",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        {[
                ["overview", "Overview"],
                ["kpi", "KPI"],
                ["profile", "Profile"],
              ["contacts", "Contacts"],
                ["notes", "Notes"],
                ["workforce", "Workforce"],
                ["shifts", "Shifts"],
                ["financial", "Financial"],
                ["invoices", "Invoices"],
                ["compliance", "Compliance"],
                ["documents", "Documents"],
               ["incidents", "Incidents"],
                ["communications", "Communications"],
                ["activity", "Activity"],
         ["ai", "Intelligence"],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() =>
              setFacility360Section(key)
            }
            style={{
              ...buttonBaseStyle(),
              minHeight: 34,
              height: 34,
              padding: "6px 11px",
              whiteSpace: "nowrap",
              background:
                facility360Section === key
                  ? "#2563eb"
                  : "#ffffff",
              color:
                facility360Section === key
                  ? "#ffffff"
                  : "#0f172a",
              border:
                facility360Section === key
                  ? "1px solid #2563eb"
                  : "1px solid #d8e1ea",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Overview placeholder */}
            {facility360Section === "overview" ? (
              <Facility360Overview
                facility={editingFacility}
                shifts={shifts}
                users={users}
              />
            ) : null}

            {/* Facility KPI Dashboard */}
            {facility360Section === "kpi" ? (
              <Facility360KPI
                facility={editingFacility}
                shifts={shifts}
                users={users}
                showMargin={canViewMargins(profile)}
              />
            ) : null}

{/* Profile */}
{facility360Section === "profile" ? (
  <Facility360Profile
    onSaveTimeZone={async timeZone => {
      if (!US_TIME_ZONES.some(([value]) => value === timeZone)) throw new Error('Choose a supported timezone.');
      const id = editingFacility?.id || editingFacility?.facilityId;
      if (!id || creatingFacility) throw new Error('Save the new facility before setting its timezone.');
      // Same facility document authorization; no financial fields or snapshots are written.
      await updateDoc(doc(db, 'facilityProfiles', id), {timeZone});
      setEditingFacility(previous => ({...previous, timeZone}));
      setFacilities(previous => previous.map(facility => (facility.id || facility.facilityId) === id ? {...facility, timeZone} : facility));
    }}
    facility={editingFacility}
    onChangeFacility={setEditingFacility}
    layoutMid={layoutMid}
  />
) : null}

{/* Contacts */}
{facility360Section === "contacts" ? (
  <Facility360Contacts
    facility={editingFacility}
    onChangeFacility={setEditingFacility}
  />
) : null}

{/* Facility Client Notes */}
{facility360Section === "notes" ? (
  <Facility360Notes
    facility={editingFacility}
    onChangeFacility={setEditingFacility}
    currentUser={profile}
  />
) : null}

{/* Workforce */}
{facility360Section === "workforce" ? (
  <Facility360Workforce
    facility={editingFacility}
    users={users}
    onChangeFacility={setEditingFacility}
  />
) : null}

{/* Shifts */}
{facility360Section === "shifts" ? (
  <Facility360Shifts
    facility={editingFacility}
    shifts={shifts}
    users={users}
  />
) : null}

{/* Financial */}
{facility360Section === "financial" ? (
  <Facility360Financial
    facility={editingFacility}
    shifts={shifts}
    showMargin={canViewMargins(profile)}
  />
) : null}

{/* Invoices */}
{facility360Section === "invoices" ? (
  <Facility360Invoices
    facility={editingFacility}
    invoices={invoices}
  />
) : null}

{/* Compliance */}
{facility360Section === "compliance" ? (
  <Facility360Compliance
    facility={editingFacility}
    users={users}
  />
) : null}

{/* Facility Documents */}
{facility360Section === "documents" ? (
  <Facility360Documents
    facility={editingFacility}
    onChangeFacility={setEditingFacility}
  />
) : null}

{/* Facility Incident History */}
{facility360Section === "incidents" ? (
  <Facility360Incidents
    facility={editingFacility}
    incidents={incidents}
  />
) : null}

{/* Facility Communication History */}
{facility360Section === "communications" ? (
  <Facility360Communications
    facility={editingFacility}
    communications={communications}
  />
) : null}

{/* Activity */}
{facility360Section === "activity" ? (
  <Facility360Activity
    facility={editingFacility}
    shifts={shifts}
    users={users}
  />
) : null}

{/* AI */}
{facility360Section === "ai" ? (
  <Facility360AIInsights
    facility={editingFacility}
    shifts={shifts}
    users={users}
    showMargin={canViewMargins(profile)}
  />
) : null}

    </div>
  </div>
) : null}

</>

  </div>
) : null}

{!loading && activeTab === "invitations" ? (
  <AdminInviteConsole
    selectedTenant={selectedTenant}
    selectedFacilityId={
      selectedFacility || ""
    }
    invites={invitedUsers}
  />
) : null}

    </div>
  </EnterpriseShell>
);
}